import {
  endReasonMayHaveWinner,
  type JewelSocketProfile,
  PVP_ABANDON_CAUSES,
  PVP_END_REASONS,
  PVP_PROFILE_LIMITS,
  PVP_TEAMS,
  type PvpAbandonCause,
  type PvpEndReason,
  type PvpParticipantId,
  type PvpRuleset,
  type PvpTeam,
  type PvpTeamSlot,
  parseJewelSocketProfile,
  pvpParseParticipantId,
  pvpParseTeam,
  pvpParticipantId,
} from '@kenocyber/shared';
import { CARD_CHARGE_FULL_BPS } from '../cards/balance';
import { CARD_EFFECT_SLOTS } from '../cards/effects';
import { LIFESTEAL_CARRY_MODULUS } from '../damage/lifesteal';
import { POISON_TICK_COUNT } from '../damage/poison';
import { SAW_INITIAL_PHASE, SAW_MAX_BLADES, SAW_ORBIT_PHASE_UNITS } from '../damage/saw';
import type { SimulationRngState } from '../simulation-rng';
import {
  PVP_ARENA_HEIGHT_MU,
  PVP_ARENA_WIDTH_MU,
  PVP_BOMB_KNOCKBACK_MU,
  PVP_BOMB_KNOCKBACK_TICKS,
  PVP_MAX_PROJECTILES,
  PVP_MISSILE_LIFETIME_TICKS,
  PVP_RUNE_KINDS,
  PVP_RUNE_PADS,
  type PvpRuneKind,
} from './balance';
import { PVP_MILLI_TICKS_PER_TICK } from './combat-profile';
import { PVP_MAX_MISSILES } from './missile';
import { type PvpTeamResult, pvpCanonicalSeats } from './roster';
import { VECTOR_SCALE } from './vectors';

/**
 * The snapshot a TEAM room is written in, and the only one that carries its own
 * version.
 *
 * ## Which of the three snapshots this is
 *
 * There are three versioned things in a duel and confusing them is how a room
 * comes back as a different match:
 *
 * 1. **The simulation's snapshot** - this file, and `PvpSnapshot` in
 *    `legacy-duel.ts`. Everything that decides the future of the fight.
 * 2. **The room's serialized state** - `PvpRoomPersistedState`, stamped
 *    `v: PVP_ROOM_STATE_VERSION`. It EMBEDS a simulation snapshot and adds what
 *    the simulation does not know: the lifecycle state, the sub-tick
 *    accumulator, when each seat dropped, whether the ending was announced.
 * 3. **The Durable Object's storage** - `apps/api/src/pvp/room-object.ts`,
 *    which writes the room's state under one key and therefore has no version
 *    of its own; the room's `v` is the version on disk.
 *
 * This round versions **only the first**, and only for `team_3v3`. The room is
 * still `v: 2`, still refuses to run a team match, and still writes the historic
 * two-seat shape - see `snapshot-layers.test.ts`, which fails if any of that
 * changes by accident.
 *
 * ## Why the historic snapshot is not given a version instead
 *
 * Because it has none, and inventing one would mean either rewriting every room
 * already in Durable Object storage or guessing at what is on disk. Neither is
 * necessary: the historic shape is identified by its CONTAINER, which does carry
 * a version (`PvpRoomPersistedState.v === 2`), and it is decoded by one function
 * named for what it is - `PvpSimulation.restore`, the v2 legacy decoder. There
 * is no sniffing, no "does it have a `version` field", and no heuristic anywhere
 * else in the codebase.
 *
 * ## What this format is
 *
 * PLAIN DATA. No `Map`, no `Set`, no class instance, no function, no `bigint`,
 * and no reference shared with the running simulation: `JSON.parse(JSON
 * .stringify(snapshot))` is the same snapshot, and mutating either side changes
 * nothing about the other. That is asserted rather than intended
 * (`snapshot-v3.test.ts`).
 *
 * ## What it deliberately does not carry
 *
 * No account, no cookie, no ticket, no invite code, no secret, no wallet and
 * nothing economic. A duel pays nothing, and a re-simulation must be runnable by
 * a process that has never heard of the players - which is the property that
 * makes "the server is right" checkable offline rather than a promise.
 *
 * The sub-tick accumulator is not here either, and that is the layer boundary
 * rather than an omission: it is wall-clock time the ROOM owes its next tick,
 * and the simulation has never known about it. `tick` is the whole of this
 * simulation's clock.
 */

/**
 * The version this build writes. A reader that meets another one refuses.
 *
 * Versão 9: cada casco ganha os dois restos do Roubo de Vida (gate V28G.I), e a
 * mesma sala passa a curar quem acerta. Um leitor da versão 8 aceitaria um
 * documento desta como sendo seu, deitava os dois restos fora, e continuava a
 * partida sem cura nenhuma - o que é a definição de "aceitar um documento como
 * sendo outro" que este campo existe para tornar impossível.
 *
 * Versão 10: o Núcleo Atômico (gate V28G.J). Três coisas novas, e as três
 * decidem o FUTURO da partida:
 *
 * - um projétil pode ser uma **bomba**, com a magnitude da explosão congelada.
 *   Um leitor da versão 9 lia-a como um tiro comum: ela voava depressa demais,
 *   batia em vez de detonar, e não explodia;
 * - um casco pode estar a ser **empurrado**, com direção, distância por
 *   percorrer e tiques por cumprir. Um leitor da versão 9 devolvia-lhe o
 *   controlo no instante em que a sala reabrisse;
 * - a sala carrega os **fluxos da rolagem por salva**. Sem eles, uma reconexão
 *   recomeçava a sequência do princípio.
 *
 * Versão 11: o Arsenal Ígneo (gate V28G.L). Três coisas novas, e as três
 * decidem o FUTURO da partida:
 *
 * - a sala carrega os **mísseis em voo**, cada um com a sua posição, direção,
 *   alvo, tempo restante, identidade e dano congelado. Um leitor da versão 10
 *   não os lia de todo: eles desapareciam da sala, e nove segundos de
 *   perseguição autoritativa evaporavam-se numa reconexão;
 * - **os ids dos mísseis saem de `nextProjectileId`**, o contador que já existia.
 *   Um espaço de ids só: a chave que uma explosão carrega tem de ser única entre
 *   TUDO o que pode explodir, e dois contadores independentes acabariam por
 *   emitir o mesmo número para uma bomba e para um míssil na mesma sala;
 * - os **fluxos de seleção de alvo**, um por cadeira, sem os quais um míssil
 *   restaurado escolheria outro inimigo do que o que a partida tinha escolhido.
 *
 * Versão 12: o Orbe da Víbora (gate V28G.M). Três coisas novas, e as três
 * decidem o FUTURO da partida:
 *
 * - a sala carrega os **stacks de veneno ativos**, cada um com a sua identidade,
 *   a sua autoria, o seu alvo, o seu total congelado, o instante em que foi
 *   aplicado e quantas das seis parcelas já pagou. Um leitor da versão 11 não os
 *   lia de todo: seis segundos de dano autoritativo evaporavam-se numa
 *   reconexão, e a marca que o jogador estava a ver desaparecia com eles;
 * - **os ids dos stacks saem de um contador PRÓPRIO**, e não de
 *   `nextProjectileId`. É a decisão oposta à dos mísseis, e é deliberada: o id de
 *   um míssil é a chave de deduplicação de uma EXPLOSÃO, que tem de ser única
 *   entre tudo o que pode explodir; o de um stack não deduplica evento nenhum -
 *   ele identifica uma coisa que EXISTE, para uma cena saber que a gota deste
 *   frame é a mesma gota do frame anterior. Dois espaços de nomes que nunca se
 *   encontram não podem colidir;
 * - os **fluxos de veneno**, um por cadeira, sem os quais o acerto seguinte a
 *   uma reconexão repetiria uma rolagem que a partida já tinha gasto.
 *
 * Versão 13: a Garra Hemorrágica (gate V28G.N). Quatro coisas novas, e as quatro
 * decidem o FUTURO da partida:
 *
 * - a sala carrega as **marcas de sangramento ativas**, cada uma com a sua
 *   identidade, a sua autoria, o seu alvo, o seu dano congelado e o instante em
 *   que foi aplicada. Um leitor da versão 12 não as lia de todo: uma reconexão
 *   apagava marcas que não expiram, e o jogador perdia dano acumulado que só ele
 *   podia gastar;
 * - **os ids das marcas saem de um contador PRÓPRIO**, pela mesma razão exata
 *   que os dos stacks de veneno saem do deles: um id de marca não deduplica
 *   evento nenhum - ele identifica uma coisa que EXISTE, para uma cena saber que
 *   o corte deste frame é o mesmo corte do frame anterior;
 * - os **fluxos de sangramento**, um por cadeira, sem os quais o acerto seguinte
 *   a uma reconexão repetiria uma rolagem que a partida já tinha gasto;
 * - um casco pode carregar uma **ordem de detonar** por executar
 *   (`detonateBleed`). Uma ordem chega entre dois tiques e é consumida no tique
 *   seguinte; uma sala reconstruída no meio disso, sem o campo, engolia uma ação
 *   de combate que o jogador tinha mesmo dado.
 *
 * Versão 14: a Égide Espinhosa (gate V28G.O). Duas coisas novas, e as duas
 * decidem o FUTURO da partida:
 *
 * - os **fluxos DEFENSIVOS**, um por cadeira. Sem eles, a instância hostil
 *   seguinte a uma reconexão repetiria uma rolagem que a partida já tinha gasto
 *   - e é o primeiro fluxo desta simulação cuja cadência é ditada pelo
 *   ADVERSÁRIO, portanto perdê-lo desloca a defesa de um jogador em função de
 *   quantas vezes o outro disparou;
 * - a **LINHAGEM** de cada entidade que pode ter nascido de uma reflexão: um
 *   projétil, um míssil, um stack de veneno e uma marca de sangramento. É um
 *   `boolean` por entidade, e não um contador, porque é uma identidade fechada:
 *   ou o payload nasceu hostil, ou nasceu de uma reflexão, e não há uma terceira
 *   coisa em que ele se possa transformar.
 *
 *   Perdê-la não perderia um efeito - perderia a RECUSA. Uma bomba refletida
 *   restaurada sem a linhagem voltaria a ser intercetável, e dois cascos com a
 *   joia fariam o pingue-pongue que a tabela fechada existe para tornar
 *   impossível. É por isso que ela sobe a versão em vez de ser um campo cuja
 *   ausência se lê como `false`.
 *
 * - **19** (gate V28H.L): `cardEffects`, as oito ranhuras de efeito por casco.
 *
 *   Uma Lentidão a meio, um Manto a arder, um Bastião de dois segundos. Tudo
 *   isto decide o FUTURO - onde a nave vai estar, se um tiro a acerta, se ela
 *   dispara e quanto ela cura - portanto tem de atravessar. Uma sala reaberta a
 *   meio de um Congelamento devolve o casco congelado com o tempo que lhe
 *   restava; sem o campo, devolvia-o a andar.
 *
 *   **A ausência dele NÃO é recusada**, e é a diferença para a linhagem acima.
 *   Um documento da versão 18 descreve uma partida em que nenhuma carta tinha
 *   efeito para deixar a correr - as habilidades não existiam - portanto oito
 *   ranhuras vazias é o estado EXACTO dessa partida, e não um recuo. Uma chave
 *   em falta que se lê como "nada estava a correr" é honesta exactamente quando
 *   nada podia estar a correr.
 *
 * - **20** (gate V28H.L): `chargeRemainingMs` num espaço de carta.
 *
 *   Duas cartas carregam antes de fazer o que fazem, e a carga não é a duração:
 *   enquanto ela corre a habilidade está ligada e ainda não produziu nada. Uma
 *   reconexão a meio de uma carga tem de devolver a carga a meio - nem uma
 *   esfera já lançada, nem uma carga do princípio.
 *
 *   Opcional, como `cardEffects`: um documento da 19 descreve uma partida em
 *   que nenhuma carta podia estar a carregar, e zero é o estado exacto dela.
 *
 * - **21** (gate V28H.L): `summons`, as invocações a existir na arena.
 *
 *   O Drone e o Leviatã. Elas têm posição, vida e relógio seus e podem ser
 *   atingidas, portanto uma sala reaberta sem elas perdia entidades vivas - e
 *   um Leviatã com piloto perdia o piloto lá dentro.
 *
 *   Opcional pela mesma razão que as duas acima: um documento da 20 descreve
 *   uma partida em que nenhuma invocação podia existir, e a lista vazia é o
 *   estado exacto dela.
 *
 * - **22** (gate V28H.N): `chargeStrengthBps` num espaço de carta.
 *
 *   A Lança Solar passou a ser carregada enquanto a tecla está premida, e o que
 *   ela produz cresce com o tempo aguentado. A força fixa-se no instante em que
 *   a tecla se solta, e é ela que decide quanto cada parcela cobra e quem a
 *   espessura apanha - portanto uma reconexão a meio de um feixe tem de devolver
 *   o MESMO feixe.
 *
 *   Opcional, e a ausência é CHEIA e não zero: um documento da 21 descreve um
 *   feixe que só sabia arder com tudo o que tinha. Zero reescreveria a história
 *   ao contrário, e um feixe que existiu a arder passaria a não cobrar nada.
 *
 * - **24** (gate V28H.X): mais um corpo em `cardBlast.kind`.
 *
 *   O raio do Curto-Circuito, pela mesma razão exacta do degrau anterior: a
 *   carta passou a disparar - "dispara energia elétrica" - e um valor novo num
 *   campo validado faz o leitor antigo recusar queixando-se da coisa errada.
 *
 * - **23** (gate V28H.W): dois corpos novos em `cardBlast.kind`.
 *
 *   A esfera do Congelamento e a estrela da Lentidão viajam na mesma lista de
 *   projécteis das duas que já lá estavam, e por isso o campo que diz QUAL é
 *   ganhou dois valores.
 *
 *   Este degrau é diferente dos anteriores, e é por isso que ele existe. Os
 *   outros acrescentaram CAMPOS, e um campo novo tem uma leitura conservadora
 *   para quem não o conhece - foi assim que o `cardBlast` inteiro entrou sem
 *   mover a versão. Um VALOR novo num campo que o leitor valida não tem leitura
 *   conservadora nenhuma: o leitor antigo encontra `ice_sphere` onde só admite
 *   dois nomes e recusa o documento com "não é um inteiro", que é uma queixa
 *   sobre a coisa errada.
 *
 *   Subir a versão troca essa recusa confusa pela recusa honesta que este número
 *   existe para dar: **este build lê a 23, e aquilo é outra coisa**.
 */
export const PVP_SNAPSHOT_V3_VERSION = 24;

/**
 * The rulesets this format describes.
 *
 * ## It used to be team_3v3 alone, and that was the duel's problem
 *
 * A duel persisted in the historic two-seat shape, which has no field for a
 * combat profile and counts a cooldown in whole ticks. So a duel could be TOLD
 * over the wire that its hulls differ - protocol version 3 does exactly that -
 * and then lose the difference the first time the room was written down.
 * `pvpShipToDuelShip` refused rather than dropping it, which turned a silent
 * loss into a loud one but did not make a duel storable.
 *
 * Widening this format is what fixes it, and widening rather than writing a
 * second decoder is deliberate: every rule a snapshot needs already lives here
 * once - health bounded by the hull's OWN ceiling, a cooldown floor of one
 * negative tick, profile fields bounded by the shared representation limits,
 * participant ids cross-checked against team and slot, projectile owners that
 * must be in the room. A duel decoder beside it would be a second copy of all
 * of that, and the copy that drifts is the one nobody is reading.
 *
 * The document says which one it is, so nothing has to guess: the `ruleset`
 * field was always there and always checked. What changed is that `duel_1v1`
 * is now an accepted value rather than a refusal - which is ADDITIVE. Every
 * version-4 document ever written says `team_3v3` and still decodes to exactly
 * what it always did, so the version does not move.
 */
export const PVP_SNAPSHOT_V3_RULESETS: readonly PvpRuleset[] = Object.freeze([
  'duel_1v1',
  'team_3v3',
]);

/* -------------------------------------------------------------------------- */
/*  The shape                                                                 */
/* -------------------------------------------------------------------------- */

export interface PvpEffectsV3 {
  readonly doubleDamageTicks: number;
  readonly speedTicks: number;
  readonly teleportCharges: number;
}

export interface PvpPointV3 {
  readonly x: number;
  readonly y: number;
}

/**
 * A hull's five numbers, as version 5 stores them.
 *
 * ## Why this had to be transported rather than rebuilt
 *
 * Because `maxHealth` is what the decoder checks `health` against. A decoder
 * that rebuilt the default profile would clamp a hull carrying a maximum-health
 * jewel back to the shipped 1 000 and refuse - or worse, accept - a legitimate
 * record. The ceiling a snapshot is validated against has to be the ceiling the
 * snapshot was written under, and the only way to know that is to carry it.
 *
 * The same reasoning covers the other three: a room evicted mid-match and
 * rebuilt from storage must resume with the ships it had, not with the ships
 * the balance file describes.
 *
 * Its own interface rather than a reuse of `PvpCombatProfile`: this is the
 * WIRE shape, plain JSON, and a persisted format that shared a type with the
 * running one would change meaning every time the running one grew a field.
 */
export interface PvpCombatProfileV3 {
  readonly baseDamage: number;
  /**
   * O teto deste casco, em MILLI, como STRING DECIMAL (versão 16).
   *
   * Era `maxHealth`, um `number` de pontos inteiros, e isso não conseguia
   * escrever a vida que uma conta tem: nenhuma fonte legal produz pontos
   * inteiros - o `flatMaxHealth` de um componente é sorteado por milli - e um
   * documento que guardasse 108 para um casco de 108,12 reabriria a partida com
   * um casco mais fraco do que o que a jogou.
   *
   * String pela mesma regra dos saldos: a vida máxima é a da CONTA e a
   * progressão não tem tecto, portanto um `number` de JSON perderia dígitos.
   */
  readonly maxHealthMilli: string;
  readonly speedBps: number;
  readonly fireIntervalMilliTicks: number;
  /**
   * Corações Primordiais incorporados, como STRING DECIMAL (versão 5).
   *
   * Uma string e não um número, pela mesma regra dos saldos: a contagem é
   * `numeric(40, 0)` na base e um `number` de JSON perderia dígitos acima de
   * 2^53 - o que reabriria a sala com um item mais fraco do que o jogador tem.
   *
   * Os outros quatro campos continuam números porque continuam limitados por
   * `PVP_PROFILE_LIMITS`; este não tem tecto de jogo e por isso não pode ter um
   * tipo com tecto.
   */
  readonly primordialHeartCount: string;
  /**
   * As joias de socket deste casco, agregadas (versão 6).
   *
   * Persistido, e não reconstruído, pela mesma razão que `maxHealth` é: o que
   * decide o FUTURO de uma partida tem de estar no documento. Uma sala reaberta
   * que redescobrisse as joias lendo a base daria ao jogador o casco que ele
   * tem AGORA, não o que a partida começou com - e é precisamente isso que
   * congelar existe para impedir.
   */
  readonly jewels: JewelSocketProfile;
}

export interface PvpParticipantV3 {
  readonly participantId: PvpParticipantId;
  /** The plain letter. Re-branded on decode; a brand is not a JSON value. */
  readonly team: string;
  readonly slot: number;
  readonly x: number;
  readonly y: number;
  /**
   * The held movement intent, in `{-1, 0, 1}` per axis.
   *
   * This IS the ship's velocity: a duel has no inertia, so where a hull goes
   * next is entirely "which keys is it holding" plus the scenery. Storing a
   * separate velocity would be storing a derived value that could disagree.
   */
  readonly moveX: number;
  readonly moveY: number;
  /**
   * A vida corrente, em MILLI, como STRING DECIMAL (versão 16).
   *
   * Zero IS eliminated - there is no second flag, because two sources of truth
   * for "is this ship alive" is exactly how a wreck comes back shooting. O
   * resultado é verificado contra o teto do perfil na descodificação.
   *
   * Em milli porque o teto é milli, e uma vida corrente noutra unidade seria uma
   * barra cujo numerador e denominador não falam a mesma língua.
   */
  readonly healthMilli: string;
  readonly aimX: number;
  readonly aimY: number;
  readonly cooldownMilliTicks: number;
  readonly appliedSeq: number;
  /** What this hull IS. Version 4 added it; version 5 gave it a fifth field. */
  readonly profile: PvpCombatProfileV3;
  readonly connected: boolean;
  readonly firing: boolean;
  readonly effects: PvpEffectsV3;
  /** The destination a player asked for, or `null`. A request, never a result. */
  readonly teleportTo: PvpPointV3 | null;
  /**
   * A ordem de DETONAR que este casco tem por executar (versão 13).
   *
   * Estado que decide o FUTURO, e é por isso que está aqui: uma ordem chega
   * entre dois tiques e é consumida no seguinte. Uma sala reconstruída no meio
   * disso, sem o campo, engolia uma ação de combate que o jogador deu mesmo.
   *
   * Um pedido e nunca um resultado: não diz alvo, contagem nem dano.
   */
  readonly detonateBleed: boolean;
  /*
   * `damageCarryMilli` e `healCarryMilli` SAÍRAM do formato (versão 16).
   *
   * Os dois seguravam a fração perdida ao converter milli-dano e milli-cura
   * numa barra de vida INTEIRA. A vida passou a ser milli, portanto não há
   * conversão e não há fração a segurar - e um campo que nunca mais seria gasto
   * é estado que uma sala reaberta compararia sem que ele decidisse nada.
   */

  /**
   * O resto SUB-MILLI do Roubo de Vida deste casco, em `[0, 9 999]` (versão 9).
   *
   * Estado que decide o FUTURO, e é por isso que está aqui: a percentagem de um
   * golpe produz uma fração de um MILÉSIMO de ponto de vida, e uma sala reaberta
   * sem ele voltaria a perder essa fração a cada golpe.
   */
  readonly lifestealCarryBps: number;

  /**
   * O empurrão que este casco está a sofrer, ou `null` (versão 10).
   *
   * Estado que decide o FUTURO por duas razões que não se substituem: para onde
   * a nave vai andar sem ninguém lhe tocar, e que o teclado do piloto não conta
   * enquanto durar. Uma sala reaberta sem ele devolveria o controlo cedo demais
   * e deixaria o casco parado a meio de um deslocamento que a partida começou.
   *
   * A direção viaja NORMALIZADA, na escala de vetores do duelo, e não como um
   * ângulo: um ângulo teria de ser reconstruído com trigonometria, e este
   * simulador não produz nenhuma.
   */
  readonly knockback: {
    readonly dirX: number;
    readonly dirY: number;
    readonly remainingMu: number;
    readonly ticks: number;
  } | null;
  /**
   * **A FASE da órbita das serras deste casco** (versão 15).
   *
   * Um inteiro em `[0, SAW_ORBIT_PHASE_UNITS)`, e zero para todo o casco sem o
   * Diamante Serrilhado.
   *
   * ## Porque ela viaja, quando podia ser derivada do tique
   *
   * Porque ela É derivável do tique enquanto o nível não mudar, e o nível pode
   * mudar. Uma fase recalculada de `tique x velocidade(nível)` daria um salto no
   * instante em que a velocidade mudasse; acumulada, ela continua onde estava e
   * passa a andar mais depressa a partir dali.
   *
   * E ela decide o FUTURO, que é o critério: onde as lâminas estão neste
   * instante decide quem elas tocam no seguinte. Uma sala reaberta com as serras
   * no ângulo errado atingiria outros cascos.
   *
   * A cena LÊ-A para desenhar. Ela é o único sítio de onde a posição de uma
   * lâmina pode vir - um relógio local daria a cada cliente uma órbita sua.
   */
  readonly sawPhase: number;
  /**
   * O resto INTEIRO do orçamento das serras deste casco (versão 15).
   *
   * String decimal, pela regra de sempre: ele deriva do dano-base autoritativo,
   * que não tem tecto de jogo, e uma instantânea é JSON.
   *
   * Estado que decide o FUTURO, e é por isso que está aqui: uma sala reaberta
   * sem ele voltaria a perder a mesma fração em cada contacto, e a taxa de longo
   * prazo deixaria de ser a que o orçamento promete.
   */
  readonly sawCarryMilli: string;
  /**
   * **Os três espaços de cartas deste casco** (versão 18, gate V28H.J).
   *
   * ## Porque atravessa, e o que ele decide
   *
   * O arrefecimento diz quando a carta pode voltar; a duração diz o que os dois
   * canhões estão a fazer AGORA. Uma sala reaberta sem eles devolveria uma carta
   * pronta a quem acabou de a usar, e apagaria uns canhões que ainda tinham
   * quatro segundos - as duas coisas que o gate exige que sobrevivam a uma
   * reconexão.
   *
   * ## Porque não são derivados do loadout
   *
   * Porque o loadout diz o que a carta É, e isto diz o que ela ESTÁ. Reconstruir
   * o segundo do primeiro seria inventar um instante.
   *
   * Milissegundos inteiros: o tique do duelo é 50 ms, e nada aqui divide.
   */
  readonly cards: readonly PvpCardStateV3[];
  /**
   * As identidades das ordens já aplicadas, as mais recentes primeiro.
   *
   * Atravessam porque a deduplicação tem de sobreviver a uma reconexão: sem
   * elas, um cliente que reenviasse a sua ordem depois de voltar ligaria a carta
   * uma segunda vez - e teria dois arrefecimentos pelo preço de um.
   */
  readonly cardCommands: readonly string[];
  /**
   * **As oito ranhuras de efeito das cartas** (gate V28H.L, versão 19).
   *
   * Uma Lentidão a meio, um Manto a arder, um Bastião de dois segundos: tudo
   * isto decide o FUTURO - onde a nave vai estar, se um tiro a acerta, se ela
   * dispara - portanto atravessa a instantânea inteira. Uma reconexão a meio
   * de um Congelamento devolve o casco congelado com o tempo que lhe restava,
   * e não descongelado.
   *
   * **Um par por ranhura, e as oito sempre presentes.** Tamanho fixo, como as
   * ranhuras são: um documento cujo tamanho dependesse de quantas cartas foram
   * lançadas é um documento que um dia não cabe.
   */
  readonly cardEffects: readonly PvpCardTimerV3[];
}

/**
 * Uma ranhura, como o formato a guarda (versão 19).
 *
 * A ORDEM é a de `CARD_EFFECT_SLOTS` e é o que dá significado a cada entrada -
 * o documento não repete o nome oito vezes por casco. Uma ranhura vazia é
 * `{ ms: 0, bps: 0 }`, e não uma ausência: um buraco no meio de uma lista
 * posicional deslocaria todas as seguintes.
 */
export interface PvpCardTimerV3 {
  readonly ms: number;
  readonly bps: number;
}

/**
 * Uma invocação, como o formato a guarda (versão 21).
 *
 * A vida em string decimal, como toda a vida deste jogo: a progressão não tem
 * tecto e um número de JSON tem. Tudo o resto são inteiros.
 */
export interface PvpSummonV3 {
  readonly id: number;
  readonly kind: string;
  readonly ownerParticipantId: string;
  readonly ownerTeam: string;
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
  readonly healthMilli: string;
  readonly maxHealthMilli: string;
  readonly lifeMs: number;
  readonly attackAccumulatorMs: number;
  readonly pilotParticipantId: string | null;
  readonly boardedRemainingMs: number;
}

/** O estado de UM espaço, como o formato o guarda (versão 18). */
export interface PvpCardStateV3 {
  readonly slot: string;
  readonly definitionId: string;
  readonly instanceId: string;
  readonly level: number;
  readonly cooldownRemainingMs: number;
  readonly activeRemainingMs: number;
  readonly attackAccumulatorMs: number;
  /**
   * Quanto falta de CARGA, em ms (versão 20, gate V28H.L).
   *
   * Opcional porque um documento da versão 19 não o tem, e a ausência é zero:
   * as duas cartas que carregam não existiam quando ele foi escrito.
   */
  readonly chargeRemainingMs?: number;
  /**
   * A FORÇA da carga que foi largada, em bps (versão 22, gate V28H.N).
   *
   * Opcional porque um documento da versão 21 não a tem, e a ausência é CHEIA:
   * a carga não era graduada, portanto o feixe que ele descreve ardia com tudo.
   */
  readonly chargeStrengthBps?: number;
}

/**
 * **O relógio de UM par serra→alvo, como o formato o guarda** (versão 15).
 *
 * ## Porque ele é ESTADO e não é derivável
 *
 * Porque ele é a única coisa que impede uma serra de doer duas vezes seguidas.
 * Uma sala reaberta sem estes relógios daria a todas as lâminas um contacto
 * imediato - `sawPairDue` responde "sim" a um par que nunca se tocou - e o
 * primeiro tique depois de uma reconexão pagaria um intervalo inteiro de dano de
 * uma vez.
 *
 * ## O que ele deliberadamente NÃO guarda
 *
 * A magnitude, o nível, a fase, a posição da lâmina e quantas vezes ela já
 * bateu. A magnitude é recalculada do perfil congelado a cada contacto - ao
 * contrário de um proc, ela não é congelada, porque não há intervalo nenhum
 * entre decidir e aplicar. A fase vive no casco. E a contagem não influencia
 * nada.
 */
export interface PvpSawContactV3 {
  /** Quem tem a lâmina. */
  readonly ownerParticipantId: PvpParticipantId;
  /** Qual delas, de `0` a `count - 1`. */
  readonly bladeIndex: number;
  /** Em quem ela bateu. */
  readonly targetParticipantId: PvpParticipantId;
  /** O TIQUE da última instância de dano deste par. Nunca milissegundos. */
  readonly lastContactTick: number;
}

export interface PvpProjectileV3 {
  readonly id: number;
  readonly ownerParticipantId: PvpParticipantId;
  /** Redundant with the owner and carried anyway: friendly fire asks per substep. */
  readonly ownerTeam: string;
  /**
   * O dano deste tiro, em MILLI e como string decimal (versão 5).
   *
   * Era `damage`, em pontos inteiros. Deixou de chegar: um tiro com corações
   * vale 40,4 pontos, e a versão 4 não tinha onde pôr a fração nem onde pôr os
   * quarenta dígitos que uma contagem grande produz.
   *
   * Decidido quando o tiro NASCE e nunca recalculado - é o que faz um projétil
   * já disparado manter o dano com que foi criado, mesmo que o perfil mude
   * entre o disparo e o impacto.
   */
  readonly damageMilli: string;
  /**
   * O proc congelado deste tiro, ou `null` para um tiro comum.
   *
   * Persistido porque um projétil em VOO tem de atravessar uma reconexão sem
   * mudar de ideias: recriá-lo sem o proc perderia o efeito, e recalculá-lo
   * daria outro resultado.
   *
   * `cutMilli` é o dano adicional EXATO congelado no nascimento (versão 8), e
   * viaja como string decimal pela regra de sempre. É o resultado e não a
   * receita: guardar `levelSum` obrigaria a reavaliar a fórmula ao restaurar, e
   * uma reavaliação é uma resposta à pergunta errada - "quanto vale agora" em
   * vez de "quanto valia quando este tiro nasceu".
   */
  readonly jewelProc: {
    readonly id: string;
    readonly entropy: number;
    readonly cutMilli: string;
  } | null;
  /**
   * A bomba que este projétil É, ou `null` para um tiro comum (versão 10).
   *
   * Persistido pela mesma razão que o proc é, e com uma consequência maior: a
   * bomba não é um extra pendurado no tiro, é o que o tiro é. Um documento sem
   * este campo descreve uma partida em que o projétil voa a outra velocidade,
   * bate em vez de detonar, e não explode - portanto perdê-lo não perde um
   * efeito, troca uma entidade por outra.
   *
   * `blastMilli` é o RESULTADO congelado no nascimento da salva, como string
   * decimal pela regra de sempre, e não a receita: guardar `levelSum` obrigaria
   * a reavaliar a fórmula ao restaurar, que é a pergunta errada.
   */
  /**
   * A explosão de uma CARTA, ou `null` (versão 20, gate V28H.L).
   *
   * Raio e dano próprios: ela não é a Bomba Atômica da joia, que tira os dois
   * do equilíbrio partilhado.
   */
  readonly cardBlast?: {
    /**
     * Qual das duas cartas (versão 23, gate V28H.U).
     *
     * Opcional porque um documento da 22 não o tem, e a ausência é `fireball`:
     * a Esfera é a que existe desde sempre, e um Catolé em voo num documento
     * antigo era um Catolé com a explosão da Esfera - que é exactamente o
     * defeito que este campo corrige.
     */
    readonly kind?: 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt';
    readonly radiusMu: number;
    readonly damageMilli: string;
  } | null;
  /** Quem este projétil persegue, ou `null`. Só o Catolé. */
  readonly homingTargetId?: string | null;
  readonly atomicBomb: {
    readonly entropy: number;
    readonly blastMilli: string;
  } | null;
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly life: number;
  /**
   * **A linhagem: esta bomba nasceu de uma reflexão** (versão 14).
   *
   * Falso para todo o tiro comum e para toda a bomba que uma salva produziu.
   * Verdadeiro só para a bomba EQUIVALENTE que nasce na nave refletora, e o que
   * ele guarda é a RECUSA: uma bomba refletida restaurada sem este campo voltaria
   * a ser intercetável, e dois cascos com a joia fariam o pingue-pongue que a
   * tabela fechada de `damage/reflection.ts` existe para tornar impossível.
   *
   * Um tiro comum intercetado não produz projétil nenhum, portanto este campo
   * nunca é verdadeiro sem `atomicBomb`.
   */
  readonly reflected: boolean;
}

/**
 * Um MÍSSIL em voo, como o documento o guarda (versão 11).
 *
 * ## Porque é uma lista à parte dos projéteis
 *
 * Porque é uma entidade à parte na simulação, e a instantânea é uma transcrição
 * do que decide o futuro - não uma reorganização dele. Um míssil não tem `vx` e
 * `vy` porque a sua magnitude é constante e o que muda é a DIREÇÃO; tem um alvo,
 * que um projétil nunca teve; e não tem proc nem bomba, porque um míssil não
 * origina joia nenhuma.
 *
 * Enfiá-lo na lista dos projéteis obrigaria cada tiro comum a carregar quatro
 * campos nulos, e a mudança de forma seria paga por todos os documentos de todas
 * as salas para descrever a entidade mais rara que existe.
 *
 * ## Os campos, e porque são exatamente estes
 *
 * São a lista que o contrato manda sobreviver a uma instantânea, a uma
 * reconexão e a uma retoma: **posição, velocidade, alvo, tempo restante e
 * identidade**. `damageMilli` junta-se-lhes pela lei que congela toda a
 * magnitude no nascimento - recalculá-la ao restaurar responderia "quanto vale
 * agora" em vez de "quanto valia quando este míssil saiu".
 */
export interface PvpMissileV3 {
  readonly id: number;
  readonly ownerParticipantId: PvpParticipantId;
  /** Redundante com o dono, e verificado contra ele, como num projétil. */
  readonly ownerTeam: string;
  /** O dano congelado, string decimal pela regra de sempre. */
  readonly damageMilli: string;
  /** A entropia congelada com a seleção. */
  readonly entropy: number;
  /**
   * Quem ele persegue, ou `null` no instante em que ficou sem alvo.
   *
   * Um `null` não sobrevive a um tique na simulação - um míssil sem alvo explode
   * - mas o campo aceita-o na mesma: recusá-lo obrigaria o codificador a
   * inventar um alvo para um estado que a simulação pode tocar, e um alvo
   * inventado é pior do que uma ausência declarada.
   */
  readonly targetParticipantId: PvpParticipantId | null;
  readonly x: number;
  readonly y: number;
  /** A direção normalizada. Nunca um ângulo: este simulador não produz nenhum. */
  readonly dirX: number;
  readonly dirY: number;
  /** Tiques que faltam antes de ele explodir sozinho, sem dano. */
  readonly life: number;
  /**
   * **A linhagem: este míssil nasceu de uma reflexão** (versão 14).
   *
   * O que ele guarda é a RECUSA, pela mesma razão exata que a de um projétil: um
   * míssil refletido restaurado sem este campo voltaria a ser intercetável, e a
   * profundidade máxima de um deixaria de ser verdade depois de uma reconexão.
   */
  readonly reflected: boolean;
}

/**
 * **Um stack de veneno como o DOCUMENTO o guarda** (versão 12).
 *
 * ## Os campos, e porque são exatamente estes
 *
 * São o que o contrato manda sobreviver a uma instantânea, a uma reconexão e a
 * uma retoma: **identidade, atacante, alvo, dano total congelado, instante de
 * aplicação e parcelas já pagas**.
 *
 * ## Porque `nextTickAt` e `ticksRemaining` NÃO são campos
 *
 * Porque são funções exatas dos dois últimos: a parcela `i` vence em
 * `appliedAt + i x 20` tiques, e faltam `6 - ticksDone`. Guardá-los seria
 * guardar o mesmo facto três vezes num documento que atravessa disco - e três
 * cópias de um facto são duas oportunidades de ele passar a discordar de si
 * mesmo. `damage/poison.ts` expõe as duas leituras como funções, que é onde a
 * derivação tem um dono só.
 *
 * ## O total atravessa como STRING
 *
 * Pela regra de sempre: ele deriva do dano-base autoritativo, que não tem tecto
 * de jogo - a contagem de Corações Primordiais vive em `numeric(40, 0)` - e uma
 * instantânea é JSON, que não tem `bigint`.
 */
export interface PvpPoisonV3 {
  readonly id: number;
  /** Quem o aplicou. Sobrevive à morte e à desconexão de quem o aplicou. */
  readonly attackerParticipantId: PvpParticipantId;
  /** Em quem. A morte deste casco leva todos os stacks dele. */
  readonly targetParticipantId: PvpParticipantId;
  /** O total congelado, string decimal pela regra de sempre. */
  readonly totalDamageMilli: string;
  /** O TIQUE da aplicação. O relógio deste modo, nunca milissegundos. */
  readonly appliedAt: number;
  /** Quantas das seis já foram cobradas, de 0 a 6. */
  readonly ticksDone: number;
  /**
   * **A linhagem: esta marca nasceu de uma reflexão** (versão 14).
   *
   * O que ele guarda é a RECUSA das PARCELAS. Um stack refletido restaurado sem
   * este campo teria as suas seis parcelas outra vez elegíveis, e a marca que já
   * tinha sido virada uma vez poderia ser virada de novo em cada segundo.
   */
  readonly reflected: boolean;
}

/**
 * **Uma marca de sangramento, como esta instantânea a guarda** (versão 13).
 *
 * Cinco campos, e são exatamente o que o contrato manda sobreviver a uma
 * instantânea, a uma reconexão e a uma retoma: **identidade, autor, alvo, dano
 * congelado e instante de aplicação**.
 *
 * ## Porque NÃO há um campo de tempo restante
 *
 * Porque uma marca não expira. Ela não tem prazo, não se renova e não cobra
 * parcela nenhuma: o que a consome é uma ordem do autor. Um campo de duração
 * aqui seria uma promessa que nada cumpriria, e um leitor futuro acabaria por
 * lhe dar significado.
 *
 * ## O dano atravessa como STRING
 *
 * Pela regra de sempre: ele deriva do dano-base autoritativo, que não tem tecto
 * de jogo - a contagem de Corações Primordiais vive em `numeric(40, 0)` - e uma
 * instantânea é JSON, que não tem `bigint`. É ele, e nunca o nível que o
 * produziu, porque uma detonação SOMA o que estava congelado e não reavalia
 * nada.
 */
export interface PvpBleedingV3 {
  readonly id: number;
  /** Quem a aplicou, e o único que a pode detonar. */
  readonly attackerParticipantId: PvpParticipantId;
  /** Em quem. A morte de qualquer um dos dois leva a marca. */
  readonly targetParticipantId: PvpParticipantId;
  /** O dano congelado, string decimal pela regra de sempre. */
  readonly damageMilli: string;
  /** O TIQUE da aplicação. O relógio deste modo, nunca milissegundos. */
  readonly appliedAt: number;
  /**
   * **A linhagem: esta marca nasceu de uma reflexão** (versão 14).
   *
   * O que ele guarda é a RECUSA da DETONAÇÃO. Uma detonação separa as duas
   * linhagens e só a original pode ser intercetada; sem este campo, uma marca
   * refletida restaurada voltaria à metade elegível e uma segunda reflexão
   * tornar-se-ia possível na ordem seguinte.
   */
  readonly reflected: boolean;
}

export interface PvpRuneV3 {
  readonly pad: number;
  readonly kind: PvpRuneKind | null;
  readonly readyAt: number;
}

/** Who abandoned and how, as this format stores it. Ids stay raw strings here. */
export interface PvpAbandonmentV3 {
  readonly cause: PvpAbandonCause;
  readonly participantId: string;
}

export interface PvpResultV3 {
  /** The winning team's letter, or `null` for every draw and every no-result. */
  readonly winner: string | null;
  readonly reason: PvpEndReason;
  readonly ticks: number;
  /**
   * Why an abandonment happened, `null` otherwise - and absent from a record
   * written before this build.
   *
   * The version did NOT move for it, on the same argument `leftAt` was added
   * under: the format did not change, it gained a field whose absence has an
   * exact and correct meaning. A bump would refuse every room in flight when
   * this shipped, which is a worse answer than reading them as what they are -
   * abandonments this build cannot explain.
   */
  readonly abandon: PvpAbandonmentV3 | null;
}

export interface PvpSnapshotV3 {
  readonly version: number;
  readonly ruleset: string;
  readonly tick: number;
  /** Exactly six, in canonical order: team then slot. */
  readonly participants: readonly PvpParticipantV3[];
  readonly projectiles: readonly PvpProjectileV3[];
  readonly nextProjectileId: number;
  readonly runes: readonly PvpRuneV3[];
  readonly rng: SimulationRngState;
  /**
   * A semente da SALA, e os fluxos de proc de cada cadeira (gate V28G.F).
   *
   * A semente porque um upgrade de um documento anterior tem de conseguir criar
   * os fluxos que ele não guardava, e derivá-los exige a semente que lhes deu
   * origem. Ela é pública e determinística - é a mesma que já governa as runas.
   *
   * Os fluxos porque uma reconexão tem de retomar EXATAMENTE onde estava: um
   * fluxo recriado do zero repetiria a sequência desde o princípio, e o mesmo
   * disparo daria outro proc.
   */
  readonly seed: string;
  readonly procRng: Readonly<Record<string, SimulationRngState>>;
  /**
   * Os fluxos da rolagem POR SALVA, um por cadeira (versão 10).
   *
   * Um mapa próprio e não uma entrada em `procRng`: são geradores diferentes,
   * com rótulos diferentes, e juntá-los faria um deles avançar a sequência do
   * outro. Retomam onde estavam, pela mesma razão que os outros retomam.
   */
  readonly bombRng: Readonly<Record<string, SimulationRngState>>;
  /** Os mísseis em voo, na ordem em que a sala os tem (versão 11). */
  readonly missiles: readonly PvpMissileV3[];
  /**
   * Os fluxos de SELEÇÃO DE ALVO, um por cadeira (versão 11).
   *
   * Um mapa próprio pela mesma razão que `bombRng` é um: são geradores
   * diferentes com rótulos diferentes, e juntá-los faria um deles adiantar a
   * sequência do outro. Retomam onde estavam, ou um míssil restaurado escolheria
   * um alvo que a partida não escolheu.
   */
  readonly missileRng: Readonly<Record<string, SimulationRngState>>;
  /** Os stacks de veneno ativos, em ordem de criação (versão 12). */
  readonly poison: readonly PvpPoisonV3[];
  /**
   * A identidade do próximo stack (versão 12).
   *
   * Um contador próprio, ao lado de `nextProjectileId` e nunca partilhado com
   * ele. A razão está na nota da versão 12: um id de míssil deduplica uma
   * explosão e tem de ser único entre tudo o que rebenta; um id de stack
   * identifica uma coisa que existe, e é lido por uma cena para reconhecer a
   * mesma gota entre dois frames.
   */
  readonly nextPoisonId: number;
  /**
   * Os fluxos do VENENO, um por cadeira (versão 12).
   *
   * Um mapa próprio pela mesma razão que `bombRng` e `missileRng` são: são
   * geradores diferentes com rótulos diferentes, e juntá-los faria um deles
   * adiantar a sequência do outro. Retomam onde estavam, ou o acerto seguinte a
   * uma reconexão repetiria uma rolagem já gasta.
   */
  readonly poisonRng: Readonly<Record<string, SimulationRngState>>;
  /** As marcas de sangramento ativas, em ordem de criação (versão 13). */
  readonly bleeding: readonly PvpBleedingV3[];
  /**
   * A identidade da próxima marca (versão 13).
   *
   * Um contador próprio, ao lado de `nextProjectileId` e de `nextPoisonId` e
   * nunca partilhado com nenhum dos dois. A razão é a da versão 12: um id de
   * míssil deduplica uma explosão e tem de ser único entre tudo o que rebenta;
   * um id de marca identifica uma coisa que existe, e é lido por uma cena para
   * reconhecer o mesmo corte entre dois frames.
   */
  readonly nextBleedingId: number;
  /**
   * Os fluxos do SANGRAMENTO, um por cadeira (versão 13).
   *
   * Um mapa próprio pela mesma razão que `bombRng`, `missileRng` e `poisonRng`
   * são: são geradores diferentes com rótulos diferentes, e juntá-los faria um
   * deles adiantar a sequência do outro. Retomam onde estavam, ou o acerto
   * seguinte a uma reconexão repetiria uma rolagem já gasta.
   */
  readonly bleedingRng: Readonly<Record<string, SimulationRngState>>;
  /**
   * Os fluxos DEFENSIVOS da reflexão, um por cadeira (versão 14).
   *
   * Um mapa próprio pela mesma razão que `bombRng`, `missileRng`, `poisonRng` e
   * `bleedingRng` são: são geradores diferentes com rótulos diferentes, e
   * juntá-los faria um deles adiantar a sequência do outro.
   *
   * E há uma razão a mais, que só este tem: a cadência dele é ditada pelo
   * ADVERSÁRIO. Ele avança uma vez por instância hostil elegível que chega ao
   * casco, portanto partilhá-lo faria a sequência de runas, procs, veneno e
   * sangramento de um jogador depender de quantas vezes ele foi atingido.
   */
  readonly reflectRng: Readonly<Record<string, SimulationRngState>>;
  /**
   * **As invocações a existir na arena** (versão 21, gate V28H.L).
   *
   * O Drone e o Leviatã. Opcional: um documento da versão 20 descreve uma
   * partida em que nenhuma podia existir, e a lista vazia é o estado exacto
   * dela.
   */
  readonly summons?: readonly PvpSummonV3[];
  /**
   * Os relógios de contacto das serras, em ordem canónica (versão 15).
   *
   * Ordenados por `(dono, lâmina, alvo)` na escrita, para uma sala ter uma
   * representação só: a lista viva está por ordem de primeiro contacto, que
   * depende de onde as naves andaram, e duas re-simulações da mesma partida
   * podiam produzir a mesma informação por outra ordem.
   *
   * Vazia em toda a partida sem esta joia.
   */
  readonly saws: readonly PvpSawContactV3[];
  /**
   * Os fluxos das SERRAS, um por cadeira (versão 15).
   *
   * Um mapa próprio pela razão de sempre, e com uma a mais que só este tem: a
   * cadência dele é ditada pelo RELÓGIO e não por um acontecimento de combate.
   * Ele avança enquanto uma lâmina estiver encostada a um inimigo, tenha o
   * jogador disparado ou não - portanto partilhá-lo faria a sequência de procs,
   * veneno, sangramento e runas depender de quanto tempo as serras estiveram em
   * contacto.
   */
  readonly sawRng: Readonly<Record<string, SimulationRngState>>;
  /**
   * Os fluxos do CORAÇÃO VITAL, um por cadeira (versão 17).
   *
   * Um mapa próprio pela razão de sempre, e com uma a mais que só este tem: o
   * LADO. Os sete acima sorteiam do lado de quem ataca ou de quem defende, e
   * competem entre si por "o que este disparo produz" ou "este golpe chega a
   * acontecer-me". Este responde a uma pergunta que não compete com nenhuma -
   * *«esta salva também curou alguém?»* - e é por isso que ele não é um intervalo
   * disjunto de nenhuma loteria: uma salva pode legitimamente lançar um míssil E
   * curar, e dois acontecimentos simultâneos precisam de dois sorteios.
   *
   * Vazio numa sala que nunca disparou. A partir da primeira salva ele existe
   * para todos os cascos que dispararam, com joia ou sem ela: a rolagem é
   * consumida sempre, e é isso que faz a sequência não depender do equipamento.
   */
  readonly vitalRng: Readonly<Record<string, SimulationRngState>>;
  readonly result: PvpResultV3 | null;
}

/* -------------------------------------------------------------------------- */
/*  Refusals                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Every way a v3 snapshot can be wrong.
 *
 * One code per rule, and every one of them REFUSES. Nothing here is repaired,
 * clamped, defaulted or normalised into something plausible: a snapshot that had
 * to be corrected to load is a snapshot describing a match that never happened,
 * and the player who lost it would have no way to know.
 */
export type PvpSnapshotV3Refusal =
  | 'not_an_object'
  | 'unknown_version'
  | 'unsupported_ruleset'
  | 'unexpected_key'
  | 'missing_key'
  | 'not_an_integer'
  | 'out_of_range'
  | 'wrong_participant_count'
  | 'duplicate_participant_id'
  | 'duplicate_team_slot'
  | 'slot_not_in_ruleset'
  | 'participant_id_mismatch'
  | 'participants_out_of_order'
  | 'unknown_team'
  | 'unknown_rune_kind'
  | 'unknown_pad'
  | 'unknown_end_reason'
  | 'projectile_owner_unknown'
  | 'projectile_owner_team_mismatch'
  | 'too_many_projectiles'
  | 'result_winner_impossible'
  | 'result_contradicts_health'
  /* Os espaços de cartas (versão 18, gate V28H.J). */
  | 'not_an_array'
  | 'too_many_cards'
  | 'wrong_card_effect_count'
  | 'not_a_string'
  | 'too_many_summons'
  | 'unknown_summon_kind'
  | 'unknown_card_slot'
  | 'duplicate_card_slot'
  | 'unknown_card'
  | 'unknown_card_instance'
  | 'too_many_card_commands'
  | 'unknown_card_command';

export class PvpSnapshotV3Error extends RangeError {
  override readonly name = 'PvpSnapshotV3Error';

  constructor(
    readonly code: PvpSnapshotV3Refusal,
    message: string,
  ) {
    super(message);
  }
}

function refuse(code: PvpSnapshotV3Refusal, message: string): never {
  throw new PvpSnapshotV3Error(code, message);
}

/* -------------------------------------------------------------------------- */
/*  Strict primitives                                                         */
/* -------------------------------------------------------------------------- */

function asObject(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    refuse('not_an_object', `${what} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Exactly these keys - no more, and no fewer.
 *
 * The EXTRA half is the one worth having. A snapshot carrying a field this build
 * does not read is a snapshot written by a build that meant something by it, and
 * silently ignoring it is how two versions come to disagree about a match while
 * both think they loaded it.
 */
/*
 * `LEGACY_DISCARDED_KEYS` era aqui, com os três campos da mecânica removida.
 *
 * O comentário dela dizia que a lista só podia ENCOLHER e que desapareceria com
 * os próprios campos. É este o gate (V28H.H.1): a mecânica foi descartada por
 * decisão de produto, e com ela a última tolerância que a nomeava.
 *
 * A partir daqui `requireExactKeys` é exatamente o que o nome diz - nenhuma
 * chave é saltada, e um documento que carregue um dos três campos é recusado
 * com `unexpected_key`, como qualquer outro campo que ninguém declarou.
 */

/**
 * @param optional Keys this build WRITES that an older record may not carry.
 *   A record predating them is not malformed - it is a record from before the
 *   field existed, and its absence has an exact meaning the caller states.
 */
function requireExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
  what: string,
  optional: readonly string[] = [],
): void {
  const allowed = new Set(keys);
  const mayBeAbsent = new Set(optional);
  for (const key of Object.keys(value)) {
    if (!allowed.has(key)) {
      refuse('unexpected_key', `${what} carries an unknown key: ${key}`);
    }
  }
  for (const key of keys) {
    if (!(key in value) && !mayBeAbsent.has(key)) {
      refuse('missing_key', `${what} is missing ${key}`);
    }
  }
}

/** An integer, and nothing that merely looks like one. */
function integer(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    /*
     * `Number.isSafeInteger` is false for NaN, for both infinities, for every
     * fraction and for anything past 2^53 - which is the whole list of ways a
     * number can arrive here and produce a simulation nobody can reproduce.
     */
    refuse('not_an_integer', `${what} must be a safe integer, got ${String(value)}`);
  }
  return value;
}

/**
 * A canonical non-negative decimal, as a string (versão 5).
 *
 * ## Porque um formato de persistência ganhou uma string numérica
 *
 * Porque dois valores deste documento deixaram de caber num `number`: a
 * contagem de Corações Primordiais, guardada em `numeric(40, 0)`, e o dano em
 * milli de um tiro que a inclui. Um `number` de JSON arredondaria os dois acima
 * de 2^53, e reabrir uma sala com um item mais fraco do que o jogador tem é
 * exatamente o teto silencioso que o `CLAUDE.md` proíbe.
 *
 * ## Canónica, e recusada em vez de reparada
 *
 * Sem sinal, sem zeros à esquerda, sem espaços, sem ponto decimal e sem
 * notação científica - cada uma dessas formas significa uma coisa diferente do
 * que parece, e um documento que precisou de ser corrigido para carregar é um
 * documento que descreve uma partida que não aconteceu.
 *
 * O comprimento é limitado por PAYLOAD e não por jogo: 80 dígitos é o dobro da
 * capacidade de armazenamento, largo o suficiente para nunca recusar um valor
 * real e estreito o suficiente para um documento hostil não entregar um inteiro
 * de um megabyte ao parser.
 */
const MAX_DECIMAL_DIGITS = 80;
const CANONICAL_DECIMAL = /^(0|[1-9][0-9]*)$/;

function decimal(value: unknown, what: string): string {
  if (typeof value !== 'string' || !CANONICAL_DECIMAL.test(value)) {
    refuse('not_an_integer', `${what} must be a canonical decimal string, got ${String(value)}`);
  }
  if (value.length > MAX_DECIMAL_DIGITS) {
    refuse('out_of_range', `${what} has ${value.length} digits, over ${MAX_DECIMAL_DIGITS}`);
  }
  return value;
}

/**
 * Uma vida em MILLI, como o formato a guarda: decimal canónico e positivo.
 *
 * Sem tecto de magnitude, e é a decisão: a vida máxima é a da CONTA e a
 * progressão não tem limite. O que existe é um comprimento de PAYLOAD, para que
 * um documento hostil não entregue ao parser um inteiro de um megabyte.
 */
function positiveDecimal(value: unknown, what: string): string {
  const text = decimal(value, what);
  if (text === '0' || text.startsWith('-')) {
    refuse('out_of_range', `${what} must be at least one milli, got ${text}`);
  }
  return text;
}

/**
 * A vida corrente, contra o teto do casco que o documento já declarou.
 *
 * Zero é legal - é um destroço, e é o que a instantânea de uma nave abatida
 * guarda. Acima do teto não é: seria um documento que a simulação nunca
 * escreveu, e aceitá-lo reabriria a partida com uma barra que passa do fim.
 */
function healthWithinHull(value: unknown, what: string, ceilingMilli: string): string {
  const text = decimal(value, what);
  if (text.startsWith('-')) {
    refuse('out_of_range', `${what} is never negative, got ${text}`);
  }
  if (BigInt(text) > BigInt(ceilingMilli)) {
    refuse('out_of_range', `${what} is above the hull ceiling ${ceilingMilli}, got ${text}`);
  }
  return text;
}

function bounded(value: unknown, what: string, low: number, high: number): number {
  const number = integer(value, what);
  if (number < low || number > high) {
    refuse('out_of_range', `${what} must be between ${low} and ${high}, got ${number}`);
  }
  return number;
}

function boolean(value: unknown, what: string): boolean {
  if (typeof value !== 'boolean') {
    refuse('not_an_integer', `${what} must be a boolean`);
  }
  return value;
}

function array(value: unknown, what: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    refuse('not_an_object', `${what} must be an array`);
  }
  return value;
}

function team(value: unknown, what: string): PvpTeam {
  const parsed = typeof value === 'string' ? pvpParseTeam(value) : null;
  if (parsed === null) {
    refuse('unknown_team', `${what} must be a team letter, got ${String(value)}`);
  }
  return parsed;
}

const POINT_KEYS = ['x', 'y'] as const;

function point(value: unknown, what: string): PvpPointV3 {
  const raw = asObject(value, what);
  requireExactKeys(raw, POINT_KEYS, what);
  return { x: integer(raw.x, `${what}.x`), y: integer(raw.y, `${what}.y`) };
}

const PROFILE_KEYS = [
  'baseDamage',
  'maxHealthMilli',
  'speedBps',
  'fireIntervalMilliTicks',
  'primordialHeartCount',
  'jewels',
] as const;

/**
 * A hull's numbers, bounded by REPRESENTATION rather than by balance.
 *
 * The ceilings come from `PVP_PROFILE_LIMITS` and exist so a malformed record
 * refuses by name instead of producing a match with a hull nothing can kill.
 * They are orders of magnitude above anything the jewel budget reaches, which
 * is what keeps them from being the "cap what an item grants" that `CLAUDE.md`
 * forbids.
 */
function profile(value: unknown, what: string): PvpCombatProfileV3 {
  const raw = asObject(value, what);
  requireExactKeys(raw, PROFILE_KEYS, what);
  return {
    baseDamage: bounded(raw.baseDamage, `${what}.baseDamage`, 1, PVP_PROFILE_LIMITS.maxBaseDamage),
    /*
     * Sem tecto de magnitude, e com um comprimento de PAYLOAD (versão 16).
     *
     * A vida máxima é a da conta e a progressão é ilimitada: um máximo aqui
     * seria um teto de progressão escrito num decodificador. O que se recusa é
     * a FORMA - decimal canónico, positivo - e um payload absurdo.
     */
    maxHealthMilli: positiveDecimal(raw.maxHealthMilli, `${what}.maxHealthMilli`),
    speedBps: bounded(raw.speedBps, `${what}.speedBps`, 1, PVP_PROFILE_LIMITS.maxSpeedBps),
    fireIntervalMilliTicks: bounded(
      raw.fireIntervalMilliTicks,
      `${what}.fireIntervalMilliTicks`,
      1,
      PVP_PROFILE_LIMITS.maxFireIntervalMilliTicks,
    ),
    /*
     * Sem tecto, e a ausência é a decisão.
     *
     * Os quatro campos acima têm um limite de REPRESENTAÇÃO porque um casco
     * malformado tem de ser recusado pelo nome. Este não pode ter um: um limite
     * superior aqui seria um teto de jogo, e a contagem foi decidida sem tecto.
     * O que `decimal` garante é a FORMA - canónica, não negativa e com um
     * comprimento de payload - que é o que um formato pode garantir.
     */
    primordialHeartCount: decimal(raw.primordialHeartCount, `${what}.primordialHeartCount`),
    /*
     * O perfil de joias, validado pelo CONTRATO partilhado (versão 6).
     *
     * `parseJewelSocketProfile` é a mesma função que o bilhete e o loadout
     * usam, e é deliberado: um decodificador com as suas próprias regras seria
     * um segundo sítio onde "que joias são legais" pode ser respondido, e uma
     * sala reaberta poderia então descrever um casco que nenhuma rota aceitaria.
     *
     * Atira em vez de devolver o vazio. Um documento cujo perfil não passa o
     * contrato é um documento corrompido, e reabrir a partida com menos joias do
     * que ela tinha seria dar a alguém uma nave mais fraca a meio de uma luta.
     */
    jewels: parseJewelSocketProfile(raw.jewels),
  };
}

const EFFECTS_KEYS = ['doubleDamageTicks', 'speedTicks', 'teleportCharges'] as const;

function effects(value: unknown, what: string): PvpEffectsV3 {
  const raw = asObject(value, what);
  requireExactKeys(raw, EFFECTS_KEYS, what);
  return {
    doubleDamageTicks: bounded(raw.doubleDamageTicks, `${what}.doubleDamageTicks`, 0, 100_000),
    speedTicks: bounded(raw.speedTicks, `${what}.speedTicks`, 0, 100_000),
    teleportCharges: bounded(raw.teleportCharges, `${what}.teleportCharges`, 0, 16),
  };
}

/**
 * O proc congelado de um projétil, ou `null`.
 *
 * Descodificador EXATO: chaves fixas, tipos verificados, e recusa em vez de
 * coerção. Um objeto aberto ou uma string de payload deixariam um documento
 * malformado entrar na simulação, e o que ele decide é dano.
 */
function decodeJewelProc(
  value: unknown,
  what: string,
): { id: string; entropy: number; cutMilli: string } | null {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = asObject(value, what);
  requireExactKeys(raw, JEWEL_PROC_KEYS, what);
  if (typeof raw.id !== 'string' || raw.id.length === 0) {
    refuse('not_an_integer', `${what}.id must be a proc id`);
  }
  const cutMilli = decimalString(raw.cutMilli, `${what}.cutMilli`);
  if (cutMilli.startsWith('-')) {
    refuse('not_an_integer', `${what}.cutMilli must not be negative`);
  }
  return {
    id: raw.id as string,
    entropy: bounded(raw.entropy, `${what}.entropy`, 0, 0xffff_ffff),
    cutMilli,
  };
}

const JEWEL_PROC_KEYS = ['id', 'entropy', 'cutMilli'] as const;

/**
 * A bomba congelada de um projétil, ou `null` (versão 10).
 *
 * Descodificador EXATO, como o do proc e pela mesma razão: o que este campo
 * decide é dano em área, e um objeto aberto seria um documento malformado a
 * entrar na simulação.
 */
/**
 * A explosão de uma CARTA que um projétil carrega, ou `null` (versão 20).
 *
 * Raio e dano PRÓPRIOS, ao contrário da Bomba Atômica: uma carta traz os seus.
 * O dano é uma string decimal como todo o dano desde o gate V28G.S.1, porque a
 * progressão não tem tecto e um número JSON tem.
 */
/**
 * Um id de participante que veio do documento, ou uma recusa.
 *
 * A validação de que ele pertence à SALA é feita mais tarde, quando o roster
 * existe - aqui só se exige que seja um texto não vazio, que é o que o formato
 * consegue afirmar sozinho.
 */
function requireParticipantText(value: unknown, what: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    refuse('not_a_string', `${what} must be a non-empty string`);
  }
  return value;
}

/**
 * As invocações, uma a uma, com as chaves exactas.
 *
 * A ausência do campo é a LISTA VAZIA e não uma recusa: um documento da versão
 * 20 descreve uma partida em que nenhuma invocação podia existir.
 */
function decodeSummons(value: unknown, what: string): readonly PvpSummonV3[] {
  if (value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    refuse('not_an_array', `${what} must be an array`);
  }
  if (value.length > PVP_MAX_WIRE_SUMMONS) {
    refuse('too_many_summons', `${what} has ${value.length} summons`);
  }
  return value.map((entry, index) => {
    const where = `${what}[${index}]`;
    const raw = asObject(entry, where);
    requireExactKeys(raw, SUMMON_KEYS, where);
    const kind = raw.kind;
    if (typeof kind !== 'string' || !SUMMON_KINDS.includes(kind)) {
      refuse('unknown_summon_kind', `${where}.kind is ${String(kind)}`);
    }
    return {
      id: bounded(raw.id, `${where}.id`, 0, Number.MAX_SAFE_INTEGER),
      kind,
      ownerParticipantId: requireParticipantText(
        raw.ownerParticipantId,
        `${where}.ownerParticipantId`,
      ),
      ownerTeam: requireParticipantText(raw.ownerTeam, `${where}.ownerTeam`),
      x: bounded(raw.x, `${where}.x`, 0, PVP_ARENA_WIDTH_MU),
      y: bounded(raw.y, `${where}.y`, 0, PVP_ARENA_HEIGHT_MU),
      aimX: integer(raw.aimX, `${where}.aimX`),
      aimY: integer(raw.aimY, `${where}.aimY`),
      healthMilli: decimalString(raw.healthMilli, `${where}.healthMilli`),
      maxHealthMilli: decimalString(raw.maxHealthMilli, `${where}.maxHealthMilli`),
      lifeMs: bounded(raw.lifeMs, `${where}.lifeMs`, 0, Number.MAX_SAFE_INTEGER),
      attackAccumulatorMs: bounded(
        raw.attackAccumulatorMs,
        `${where}.attackAccumulatorMs`,
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      pilotParticipantId:
        raw.pilotParticipantId === null || raw.pilotParticipantId === undefined
          ? null
          : requireParticipantText(raw.pilotParticipantId, `${where}.pilotParticipantId`),
      boardedRemainingMs: bounded(
        raw.boardedRemainingMs,
        `${where}.boardedRemainingMs`,
        0,
        Number.MAX_SAFE_INTEGER,
      ),
    };
  });
}

function decodeCardBlast(
  value: unknown,
  what: string,
): {
  kind: 'fireball' | 'catole' | 'ice_sphere' | 'ice_star' | 'shock_bolt';
  radiusMu: number;
  damageMilli: string;
} | null {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = asObject(value, what);
  requireExactKeys(raw, CARD_BLAST_KEYS, what, CARD_BLAST_OPTIONAL_KEYS);
  const damageMilli = decimalString(raw.damageMilli, `${what}.damageMilli`);
  if (damageMilli.startsWith('-')) {
    refuse('not_an_integer', `${what}.damageMilli must not be negative`);
  }
  /*
   * A ausência é `fireball` e não uma recusa: um documento da versão 22 descreve
   * uma partida em que as duas cartas partilhavam a mesma explosão, e a Esfera é
   * a leitura conservadora - a menor das duas.
   */
  const kind = raw.kind === undefined ? 'fireball' : raw.kind;
  if (
    kind !== 'fireball' &&
    kind !== 'catole' &&
    kind !== 'ice_sphere' &&
    kind !== 'ice_star' &&
    kind !== 'shock_bolt'
  ) {
    refuse('not_an_integer', `${what}.kind is ${String(kind)}`);
  }
  return {
    kind,
    radiusMu: bounded(raw.radiusMu, `${what}.radiusMu`, 0, PVP_ARENA_WIDTH_MU),
    damageMilli,
  };
}

function decodeAtomicBomb(
  value: unknown,
  what: string,
): { entropy: number; blastMilli: string } | null {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = asObject(value, what);
  requireExactKeys(raw, ATOMIC_BOMB_KEYS, what);
  const blastMilli = decimalString(raw.blastMilli, `${what}.blastMilli`);
  if (blastMilli.startsWith('-')) {
    refuse('not_an_integer', `${what}.blastMilli must not be negative`);
  }
  return {
    entropy: bounded(raw.entropy, `${what}.entropy`, 0, 0xffff_ffff),
    blastMilli,
  };
}

/**
 * O empurrão de um casco, ou `null` (versão 10).
 *
 * Os limites não são folga: uma direção normalizada tem componentes em
 * `[-VECTOR_SCALE, VECTOR_SCALE]` por construção, a distância por percorrer não
 * pode ser maior do que o deslocamento máximo que a joia produz, e os tiques por
 * cumprir não podem ser mais do que a duração inteira. Um valor fora de qualquer
 * um deles descreve um empurrão que nenhuma explosão pode ter criado.
 */
function decodeKnockback(
  value: unknown,
  what: string,
): { dirX: number; dirY: number; remainingMu: number; ticks: number } | null {
  if (value === null || value === undefined) {
    return null;
  }
  const raw = asObject(value, what);
  requireExactKeys(raw, KNOCKBACK_KEYS, what);
  const ticks = bounded(raw.ticks, `${what}.ticks`, 1, PVP_BOMB_KNOCKBACK_TICKS);
  return {
    dirX: bounded(raw.dirX, `${what}.dirX`, -VECTOR_SCALE, VECTOR_SCALE),
    dirY: bounded(raw.dirY, `${what}.dirY`, -VECTOR_SCALE, VECTOR_SCALE),
    remainingMu: bounded(raw.remainingMu, `${what}.remainingMu`, 0, PVP_BOMB_KNOCKBACK_MU),
    ticks,
  };
}

/** Uma string decimal, recusada em vez de coagida. */
const DECIMAL_STRING = /^-?[0-9]+$/;

function decimalString(value: unknown, what: string): string {
  if (typeof value !== 'string' || !DECIMAL_STRING.test(value)) {
    refuse('not_an_integer', `${what} must be a decimal string`);
  }
  return value;
}

const RNG_KEYS = ['seed', 'state', 'drawCount'] as const;

/**
 * The generator's state, exactly as `SimulationRng.snapshot` writes it.
 *
 * The two 64-bit halves are DECIMAL STRINGS rather than `bigint`, which is what
 * makes this format survive `JSON.stringify` at all - a `bigint` throws there.
 * Their contents are `restoreSimulationRng`'s business: duplicating its parsing
 * here would be a second opinion waiting to disagree with it.
 */
function rng(value: unknown, what: string): SimulationRngState {
  const raw = asObject(value, what);
  requireExactKeys(raw, RNG_KEYS, what);
  if (typeof raw.seed !== 'string' || typeof raw.state !== 'string') {
    refuse('not_an_integer', `${what} must carry the generator's seed and state as strings`);
  }
  return {
    seed: raw.seed,
    state: raw.state,
    drawCount: bounded(raw.drawCount, `${what}.drawCount`, 0, Number.MAX_SAFE_INTEGER),
  };
}

/* -------------------------------------------------------------------------- */
/*  Decoding                                                                  */
/* -------------------------------------------------------------------------- */

const SNAPSHOT_KEYS = [
  'version',
  'ruleset',
  'tick',
  'participants',
  'projectiles',
  'nextProjectileId',
  'runes',
  'rng',
  'seed',
  'procRng',
  'bombRng',
  'missiles',
  'missileRng',
  'poison',
  'nextPoisonId',
  'poisonRng',
  'bleeding',
  'nextBleedingId',
  'bleedingRng',
  'reflectRng',
  'summons',
  'saws',
  'sawRng',
  'vitalRng',
  'result',
] as const;

const PARTICIPANT_KEYS = [
  'participantId',
  'team',
  'slot',
  'x',
  'y',
  'moveX',
  'moveY',
  'healthMilli',
  'aimX',
  'aimY',
  'cooldownMilliTicks',
  'appliedSeq',
  'connected',
  'firing',
  'effects',
  'teleportTo',
  'detonateBleed',
  'profile',
  'lifestealCarryBps',
  'knockback',
  'sawPhase',
  'sawCarryMilli',
  'cards',
  'cardCommands',
  'cardEffects',
] as const;

/**
 * As chaves que um documento ANTIGO pode não ter.
 *
 * `cardEffects` entrou na versão 19 (gate V28H.L). Um documento da 18 descreve
 * uma partida em que nenhuma carta tinha efeito para deixar a correr, portanto
 * a ausência dele não é um documento estragado - é o estado exacto daquela
 * partida, e `decodeCardEffects` devolve as oito ranhuras vazias.
 *
 * Uma lista e não uma tolerância geral: qualquer OUTRA chave em falta continua
 * a ser um documento que esta simulação não escreveu, e continua recusada.
 */
const PARTICIPANT_OPTIONAL_KEYS = ['cardEffects'] as const;

/** As chaves de UMA invocação. Exactas, como todas as outras. */
const SUMMON_KEYS = [
  'id',
  'kind',
  'ownerParticipantId',
  'ownerTeam',
  'x',
  'y',
  'aimX',
  'aimY',
  'healthMilli',
  'maxHealthMilli',
  'lifeMs',
  'attackAccumulatorMs',
  'pilotParticipantId',
  'boardedRemainingMs',
] as const;

/** As duas formas que uma invocação pode ter. Nem uma terceira. */
const SUMMON_KINDS: readonly string[] = ['drone', 'leviathan'];

/** Os três nomes de espaço, e não há um quarto. */
const PVP_CARD_SLOT_NAMES: readonly string[] = ['card_slot_1', 'card_slot_2', 'card_slot_3'];

/** Quantos espaços um casco tem. Três. */
const PVP_MAX_CARD_SLOTS = 3;

/** Quantas invocações um documento pode descrever. O tecto que a sala aplica. */
const PVP_MAX_WIRE_SUMMONS = 12;

/** O nível máximo de uma carta, do catálogo. */
const PVP_MAX_CARD_LEVEL = 6;

/** Quantas ordens um casco recorda. O mesmo teto que a sala aplica. */
const PVP_MAX_CARD_COMMANDS = 8;

/** As chaves de UM espaço de carta. Exatas, como todas as outras. */
const CARD_TIMER_KEYS = ['ms', 'bps'] as const;

/** A chave que um documento da versão 19 pode não ter. Ver `chargeRemainingMs`. */
const CARD_OPTIONAL_KEYS = ['chargeRemainingMs', 'chargeStrengthBps'] as const;

const CARD_KEYS = [
  'slot',
  'chargeRemainingMs',
  'chargeStrengthBps',
  'definitionId',
  'instanceId',
  'level',
  'cooldownRemainingMs',
  'activeRemainingMs',
  'attackAccumulatorMs',
] as const;

const KNOCKBACK_KEYS = ['dirX', 'dirY', 'remainingMu', 'ticks'] as const;

const ATOMIC_BOMB_KEYS = ['entropy', 'blastMilli'] as const;

/**
 * As chaves que um documento anterior à versão 20 pode não ter.
 *
 * As duas entraram com a Esfera Ígnea e o Catolé (gate V28H.L). Um documento
 * antigo descreve uma partida em que nenhum projétil podia carregar uma
 * explosão de carta nem perseguir ninguém, e `null` é o estado exacto dele.
 */
const PROJECTILE_OPTIONAL_KEYS = ['cardBlast', 'homingTargetId'] as const;

/** As chaves de uma explosão de carta. Exactas, como todas as outras. */
const CARD_BLAST_KEYS = ['kind', 'radiusMu', 'damageMilli'] as const;

/** A chave que um documento da versão 22 pode não ter. Ver `kind`. */
const CARD_BLAST_OPTIONAL_KEYS = ['kind'] as const;

const PROJECTILE_KEYS = [
  'id',
  'jewelProc',
  'atomicBomb',
  'cardBlast',
  'homingTargetId',
  'ownerParticipantId',
  'ownerTeam',
  'damageMilli',
  'x',
  'y',
  'vx',
  'vy',
  'life',
  'reflected',
] as const;

const MISSILE_KEYS = [
  'id',
  'ownerParticipantId',
  'ownerTeam',
  'damageMilli',
  'entropy',
  'targetParticipantId',
  'x',
  'y',
  'dirX',
  'dirY',
  'life',
  'reflected',
] as const;

const POISON_KEYS = [
  'id',
  'attackerParticipantId',
  'targetParticipantId',
  'totalDamageMilli',
  'appliedAt',
  'ticksDone',
  'reflected',
] as const;

const BLEEDING_KEYS = [
  'id',
  'attackerParticipantId',
  'targetParticipantId',
  'damageMilli',
  'appliedAt',
  'reflected',
] as const;

const SAW_KEYS = [
  'ownerParticipantId',
  'bladeIndex',
  'targetParticipantId',
  'lastContactTick',
] as const;

const RUNE_KEYS = ['pad', 'kind', 'readyAt'] as const;

const RESULT_KEYS = ['winner', 'reason', 'ticks', 'abandon'] as const;

/**
 * Result fields this build writes that a record written by an older one lacks.
 *
 * Added without a version bump, on the same argument as the room's `leftAt`: the
 * format did not CHANGE, it gained a field whose absence has an exact meaning.
 * A bump would refuse every room in flight when this shipped, and a room refused
 * is a match somebody loses to a decoder.
 */
const RESULT_KEYS_ADDED_AFTER_V3 = ['abandon'] as const;

const ABANDONMENT_KEYS = ['cause', 'participantId'] as const;

interface DecodedParticipant extends PvpParticipantV3 {
  readonly parsedTeam: PvpTeam;
  readonly parsedSlot: PvpTeamSlot;
}

function decodeParticipant(value: unknown, index: number, ruleset: PvpRuleset): DecodedParticipant {
  const what = `participants[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, PARTICIPANT_KEYS, what, PARTICIPANT_OPTIONAL_KEYS);

  const side = team(raw.team, `${what}.team`);
  const slot = integer(raw.slot, `${what}.slot`);
  /*
   * Against the DOCUMENT's ruleset, not a constant. A duel has one slot a side,
   * so `a2` is not a chair anybody could have sat in - and a snapshot naming
   * one would restore a match with a ship nobody is driving.
   */
  const ref = pvpParseParticipantId(
    typeof raw.participantId === 'string' ? raw.participantId : '',
    ruleset,
  );
  if (ref === null) {
    refuse('slot_not_in_ruleset', `${what}.participantId is not a ${ruleset} chair`);
  }
  if (ref.slot !== slot) {
    refuse(
      'slot_not_in_ruleset',
      `${what} says slot ${slot} and is called ${String(raw.participantId)}`,
    );
  }
  if (raw.participantId !== pvpParticipantId(side, ref.slot)) {
    refuse(
      'participant_id_mismatch',
      `${what} is called ${String(raw.participantId)} but sits at ${side}${slot}`,
    );
  }

  const teleportTo = raw.teleportTo === null ? null : point(raw.teleportTo, `${what}.teleportTo`);
  /*
   * The profile is decoded FIRST, because two fields below are bounded by it.
   *
   * `health` against `profile.maxHealth` and not against a module constant:
   * the ceiling a record is checked against has to be the ceiling it was
   * written under, or a hull carrying a maximum-health jewel is refused for
   * being exactly what it is.
   */
  const hull = profile(raw.profile, `${what}.profile`);
  return {
    participantId: raw.participantId as PvpParticipantId,
    team: side,
    slot: ref.slot,
    parsedTeam: side,
    parsedSlot: ref.slot,
    profile: hull,
    x: bounded(raw.x, `${what}.x`, 0, PVP_ARENA_WIDTH_MU),
    y: bounded(raw.y, `${what}.y`, 0, PVP_ARENA_HEIGHT_MU),
    moveX: bounded(raw.moveX, `${what}.moveX`, -1, 1),
    moveY: bounded(raw.moveY, `${what}.moveY`, -1, 1),
    /*
     * Contra o teto do PERFIL, e em milli. Zero é legal - é um destroço - e um
     * valor acima do teto é um documento que a simulação nunca escreveu.
     */
    healthMilli: healthWithinHull(raw.healthMilli, `${what}.healthMilli`, hull.maxHealthMilli),
    aimX: integer(raw.aimX, `${what}.aimX`),
    aimY: integer(raw.aimY, `${what}.aimY`),
    /*
     * The floor is negative by exactly one tick, and that is the accumulator
     * rather than a slack bound: the counter decrements only while positive, so
     * it lands in `(-1000, 0]` for any cadence that is not a whole tick. A
     * value below that could not have been produced by any sequence of ticks.
     */
    cooldownMilliTicks: bounded(
      raw.cooldownMilliTicks,
      `${what}.cooldownMilliTicks`,
      -PVP_MILLI_TICKS_PER_TICK,
      100_000_000,
    ),
    appliedSeq: bounded(raw.appliedSeq, `${what}.appliedSeq`, 0, Number.MAX_SAFE_INTEGER),
    connected: boolean(raw.connected, `${what}.connected`),
    firing: boolean(raw.firing, `${what}.firing`),
    effects: effects(raw.effects, `${what}.effects`),
    teleportTo,
    /* A ordem por executar, exigida pelo formato 13 (gate V28G.N). */
    detonateBleed: boolean(raw.detonateBleed, `${what}.detonateBleed`),
    /*
     * O resto SUB-MILLI do Roubo de Vida, LIMITADO (versão 9).
     *
     * Estritamente abaixo do seu módulo, por definição de resto: um valor igual
     * ou maior seria um resto que nenhuma sequência de golpes pode produzir, e
     * aceitá-lo seria aceitar um documento que a simulação não escreveu. O
     * limite sai da constante do resolvedor em vez de ser escrito à mão.
     *
     * Os outros dois restos - `damageCarryMilli` e `healCarryMilli` - saíram do
     * formato na versão 16: eles seguravam a fração perdida ao converter milli
     * numa barra INTEIRA, e a barra passou a ser milli.
     */
    lifestealCarryBps: bounded(
      raw.lifestealCarryBps,
      `${what}.lifestealCarryBps`,
      0,
      LIFESTEAL_CARRY_MODULUS - 1,
    ),
    knockback: decodeKnockback(raw.knockback, `${what}.knockback`),
    /*
     * A órbita e o resto das serras (versão 15).
     *
     * A fase é limitada ao módulo da volta e não a um inteiro qualquer: um valor
     * fora dele não pode ter sido produzido por nenhuma sequência de tiques, e
     * aceitá-lo seria aceitar um documento que a simulação não escreveu.
     *
     * O resto não tem limite superior escrito aqui, e é deliberado: o
     * denominador depende do nível, e o nível vive no perfil que este mesmo
     * documento traz - um limite escrito à mão seria uma segunda verdade sobre
     * uma conta que `sawDamageDenominator` já faz.
     */
    sawPhase: bounded(raw.sawPhase, `${what}.sawPhase`, 0, SAW_ORBIT_PHASE_UNITS - 1),
    sawCarryMilli: decimal(raw.sawCarryMilli, `${what}.sawCarryMilli`),
    /*
     * Os três espaços de cartas (versão 18, gate V28H.J).
     *
     * Uma lista de zero a três: um espaço vazio não produz entrada, exatamente
     * como no loadout congelado que a produziu. Mais do que três é um documento
     * que a simulação não escreveu, e é recusado.
     */
    cards: decodeCards(raw.cards, `${what}.cards`),
    cardCommands: decodeCardCommands(raw.cardCommands, `${what}.cardCommands`),
    /*
     * As oito ranhuras de efeito (versão 19, gate V28H.L). Exactamente oito,
     * sempre, pela ordem de `CARD_EFFECT_SLOTS` - uma lista posicional com um
     * buraco deslocaria tudo o que vem a seguir.
     */
    cardEffects: decodeCardEffects(raw.cardEffects, `${what}.cardEffects`),
  };
}

/**
 * Os três espaços, um a um, com as chaves exatas e os relógios limitados.
 *
 * Nada aqui é normalizado: um relógio negativo, um nível impossível ou um espaço
 * inventado são documentos que esta simulação não escreveu, e recusá-los é a
 * mesma disciplina que o resto deste ficheiro tem.
 */
/**
 * As oito ranhuras, uma a uma, pela ordem canónica.
 *
 * **Exactamente oito, e nunca menos**: elas são posicionais, e um documento com
 * sete deslocaria o significado de todas a partir da que faltasse. Um documento
 * antigo - de antes do gate V28H.L - não tem o campo, e isso NÃO é um erro:
 * ver `decodeCardEffects`, que devolve oito ranhuras vazias, que é o estado
 * exacto da partida que o escreveu.
 */
function decodeCardEffects(value: unknown, what: string): readonly PvpCardTimerV3[] {
  if (value === undefined) {
    /*
     * Um documento escrito antes de as cartas terem efeitos. Oito ranhuras
     * vazias é o estado exacto dessa partida, e não um recuo: nada estava a
     * correr sobre aquele casco porque nada podia estar.
     */
    return CARD_EFFECT_SLOTS.map(() => ({ ms: 0, bps: 0 }));
  }
  if (!Array.isArray(value)) {
    refuse('not_an_array', `${what} must be an array`);
  }
  if (value.length !== CARD_EFFECT_SLOTS.length) {
    refuse(
      'wrong_card_effect_count',
      `${what} has ${value.length} slots and a hull has ${CARD_EFFECT_SLOTS.length}`,
    );
  }
  return value.map((entry, index) => {
    const where = `${what}[${index}]`;
    const raw = asObject(entry, where);
    requireExactKeys(raw, CARD_TIMER_KEYS, where);
    return {
      ms: bounded(raw.ms, `${where}.ms`, 0, Number.MAX_SAFE_INTEGER),
      bps: bounded(raw.bps, `${where}.bps`, 0, 10_000),
    };
  });
}

function decodeCards(value: unknown, what: string): readonly PvpCardStateV3[] {
  if (!Array.isArray(value)) {
    refuse('not_an_array', `${what} must be an array`);
  }
  if (value.length > PVP_MAX_CARD_SLOTS) {
    refuse(
      'too_many_cards',
      `${what} has ${value.length} slots and a hull has ${PVP_MAX_CARD_SLOTS}`,
    );
  }
  const seen = new Set<string>();
  return value.map((entry, index) => {
    const where = `${what}[${index}]`;
    const raw = asObject(entry, where);
    requireExactKeys(raw, CARD_KEYS, where, CARD_OPTIONAL_KEYS);
    const slot = raw.slot;
    if (typeof slot !== 'string' || !PVP_CARD_SLOT_NAMES.includes(slot)) {
      refuse('unknown_card_slot', `${where}.slot is ${String(slot)}`);
    }
    if (seen.has(slot)) {
      refuse('duplicate_card_slot', `${where}.slot ${slot} appears twice`);
    }
    seen.add(slot);
    const definitionId = raw.definitionId;
    if (typeof definitionId !== 'string' || definitionId.length === 0) {
      refuse('unknown_card', `${where}.definitionId is ${String(definitionId)}`);
    }
    const instanceId = raw.instanceId;
    if (typeof instanceId !== 'string' || instanceId.length === 0) {
      refuse('unknown_card_instance', `${where}.instanceId is ${String(instanceId)}`);
    }
    return {
      slot,
      definitionId,
      instanceId,
      level: bounded(raw.level, `${where}.level`, 1, PVP_MAX_CARD_LEVEL),
      cooldownRemainingMs: bounded(
        raw.cooldownRemainingMs,
        `${where}.cooldownRemainingMs`,
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      ...(raw.chargeRemainingMs === undefined
        ? {}
        : {
            chargeRemainingMs: bounded(
              raw.chargeRemainingMs,
              `${where}.chargeRemainingMs`,
              0,
              Number.MAX_SAFE_INTEGER,
            ),
          }),
      ...(raw.chargeStrengthBps === undefined
        ? {}
        : {
            chargeStrengthBps: bounded(
              raw.chargeStrengthBps,
              `${where}.chargeStrengthBps`,
              0,
              CARD_CHARGE_FULL_BPS,
            ),
          }),
      activeRemainingMs: bounded(
        raw.activeRemainingMs,
        `${where}.activeRemainingMs`,
        0,
        Number.MAX_SAFE_INTEGER,
      ),
      attackAccumulatorMs: bounded(
        raw.attackAccumulatorMs,
        `${where}.attackAccumulatorMs`,
        0,
        Number.MAX_SAFE_INTEGER,
      ),
    };
  });
}

/** As identidades já aplicadas. Limitadas, como a lista que as guarda. */
function decodeCardCommands(value: unknown, what: string): readonly string[] {
  if (!Array.isArray(value)) {
    refuse('not_an_array', `${what} must be an array`);
  }
  if (value.length > PVP_MAX_CARD_COMMANDS) {
    refuse(
      'too_many_card_commands',
      `${what} has ${value.length} entries and a hull remembers ${PVP_MAX_CARD_COMMANDS}`,
    );
  }
  return value.map((entry, index) => {
    if (typeof entry !== 'string' || entry.length === 0) {
      refuse('unknown_card_command', `${what}[${index}] is ${String(entry)}`);
    }
    return entry;
  });
}

function decodeProjectile(
  value: unknown,
  index: number,
  teamOf: ReadonlyMap<PvpParticipantId, PvpTeam>,
): PvpProjectileV3 {
  const what = `projectiles[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, PROJECTILE_KEYS, what, PROJECTILE_OPTIONAL_KEYS);
  const owner = raw.ownerParticipantId;
  if (typeof owner !== 'string' || !teamOf.has(owner)) {
    refuse('projectile_owner_unknown', `${what} is owned by ${String(owner)}, who is not here`);
  }
  const declared = team(raw.ownerTeam, `${what}.ownerTeam`);
  if (teamOf.get(owner) !== declared) {
    /*
     * The owner and the team are redundant on purpose, and a disagreement is
     * exactly what the redundancy is for: a shot whose team says one thing and
     * whose owner says another would pick its victims by the wrong one.
     */
    refuse(
      'projectile_owner_team_mismatch',
      `${what} says team ${declared} and ${owner} is on ${String(teamOf.get(owner))}`,
    );
  }
  return {
    id: bounded(raw.id, `${what}.id`, 0, Number.MAX_SAFE_INTEGER),
    ownerParticipantId: owner,
    ownerTeam: declared,
    jewelProc: decodeJewelProc(raw.jewelProc, `${what}.jewelProc`),
    atomicBomb: decodeAtomicBomb(raw.atomicBomb, `${what}.atomicBomb`),
    /* A explosão de carta e a perseguição (versão 20, gate V28H.L). */
    cardBlast: decodeCardBlast(raw.cardBlast, `${what}.cardBlast`),
    homingTargetId:
      raw.homingTargetId === undefined || raw.homingTargetId === null
        ? null
        : requireParticipantText(raw.homingTargetId, `${what}.homingTargetId`),
    /*
     * Sem limite superior desde a versão 5, e é a mesma razão do perfil.
     *
     * Era `PVP_PROFILE_LIMITS.maxBaseDamage * 2`, o que era certo enquanto o
     * dano de um tiro era o base do atirador com uma runa a duplicá-lo. Deixou
     * de ser: um tiro carrega também +1 % por Coração Primordial incorporado, e
     * a contagem não tem tecto de jogo - portanto qualquer tecto aqui recusaria
     * um documento legítimo, que é uma forma de apagar um item.
     *
     * O que continua garantido é a FORMA: um decimal canónico não negativo, com
     * um comprimento de payload.
     */
    damageMilli: decimal(raw.damageMilli, `${what}.damageMilli`),
    x: integer(raw.x, `${what}.x`),
    y: integer(raw.y, `${what}.y`),
    vx: integer(raw.vx, `${what}.vx`),
    vy: integer(raw.vy, `.vy`),
    life: bounded(raw.life, `.life`, 0, 100_000),
    /* A linhagem. Um booleano exato: nada aqui é reparado nem assumido. */
    reflected: boolean(raw.reflected, `${what}.reflected`),
  };
}

/**
 * **Um relógio de contacto de serra, decodificado** (versão 15).
 *
 * As mesmas recusas de uma marca, e pelas mesmas razões: um dono que não está
 * na sala, um alvo que não está na sala, e os dois na mesma equipa são três
 * documentos que a simulação não pode ter escrito. Uma serra não toca um aliado
 * - `enemiesOf` responde à pergunta uma vez - portanto encontrá-lo aqui
 * significa que alguma fronteira falhou.
 *
 * O índice da lâmina é limitado a `[0, SAW_MAX_BLADES)`: quatro é o máximo que
 * o nível 30 mantém, e uma quinta lâmina descreveria uma órbita que nenhum
 * nível produz.
 */
function decodeSawContact(
  value: unknown,
  index: number,
  teamOf: ReadonlyMap<PvpParticipantId, PvpTeam>,
): PvpSawContactV3 {
  const what = `saws[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, SAW_KEYS, what);
  const owner = raw.ownerParticipantId;
  if (typeof owner !== 'string' || !teamOf.has(owner)) {
    refuse('projectile_owner_unknown', `${what} belongs to ${String(owner)}, who is not here`);
  }
  const target = raw.targetParticipantId;
  if (typeof target !== 'string' || !teamOf.has(target)) {
    refuse('projectile_owner_unknown', `${what} sits on ${String(target)}, who is not here`);
  }
  if (teamOf.get(owner) === teamOf.get(target)) {
    refuse(
      'projectile_owner_team_mismatch',
      `${what} has ${owner} cutting ${target}, who is on the same team`,
    );
  }
  return {
    ownerParticipantId: owner as PvpParticipantId,
    bladeIndex: bounded(raw.bladeIndex, `${what}.bladeIndex`, 0, SAW_MAX_BLADES - 1),
    targetParticipantId: target as PvpParticipantId,
    lastContactTick: bounded(
      raw.lastContactTick,
      `${what}.lastContactTick`,
      0,
      Number.MAX_SAFE_INTEGER,
    ),
  };
}

/**
 * Um míssil, validado com a mesma disciplina de um projétil (versão 11).
 *
 * Os limites não são folga. Uma direção normalizada tem componentes em
 * `[-VECTOR_SCALE, VECTOR_SCALE]` por construção; o tempo restante não pode
 * exceder a vida inteira de um míssil; e o ALVO, quando existe, tem de ser
 * alguém que está nesta sala **e do outro lado**. A última verificação é a que
 * importa: um documento que apontasse um míssil a um aliado descreveria uma
 * partida que a simulação não consegue produzir, e restaurá-lo poria fogo amigo
 * numa sala que não o tem.
 */
/**
 * Um stack de veneno, validado campo a campo.
 *
 * ## O que é RECUSADO, e nada é reparado
 *
 * - um atacante ou um alvo que não sejam cadeiras desta partida;
 * - um alvo que esteja na equipa do atacante - um casco não envenena um aliado,
 *   e um documento que o diga descreve uma partida que não aconteceu;
 * - `ticksDone` fora de `[0, 6]` - seis é toda a vida de um stack, e um sete
 *   descreveria uma sétima parcela que não existe;
 * - um total que não seja um decimal.
 *
 * ## E o que NÃO é recusado: quantos são
 *
 * Não há teto. O contrato é explícito - "não imponha `.max(N)` arbitrário à
 * quantidade de stacks" - e um limite aqui seria uma decisão de equilíbrio
 * tomada por um validador. O que os limita é a aritmética que já existe: um
 * acerto direto aplica no máximo um, e um stack vive seis segundos.
 */
function decodePoison(
  value: unknown,
  index: number,
  teamOf: ReadonlyMap<PvpParticipantId, PvpTeam>,
): PvpPoisonV3 {
  const what = `poison[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, POISON_KEYS, what);
  const attacker = raw.attackerParticipantId;
  if (typeof attacker !== 'string' || !teamOf.has(attacker)) {
    refuse(
      'projectile_owner_unknown',
      `${what} was applied by ${String(attacker)}, who is not here`,
    );
  }
  const target = raw.targetParticipantId;
  if (typeof target !== 'string' || !teamOf.has(target)) {
    refuse('projectile_owner_unknown', `${what} sits on ${String(target)}, who is not here`);
  }
  if (teamOf.get(attacker) === teamOf.get(target)) {
    refuse(
      'projectile_owner_team_mismatch',
      `${what} was applied by ${attacker} to ${target}, who is on the same team`,
    );
  }
  return {
    id: bounded(raw.id, `${what}.id`, 0, Number.MAX_SAFE_INTEGER),
    attackerParticipantId: attacker as PvpParticipantId,
    targetParticipantId: target as PvpParticipantId,
    totalDamageMilli: decimal(raw.totalDamageMilli, `${what}.totalDamageMilli`),
    appliedAt: bounded(raw.appliedAt, `${what}.appliedAt`, 0, Number.MAX_SAFE_INTEGER),
    ticksDone: bounded(raw.ticksDone, `${what}.ticksDone`, 0, POISON_TICK_COUNT),
    /* A linhagem. Um booleano exato: nada aqui é reparado nem assumido. */
    reflected: boolean(raw.reflected, `${what}.reflected`),
  };
}

/**
 * Uma marca de sangramento, decodificada (versão 13).
 *
 * ## O que é RECUSADO, e nada é reparado
 *
 * - um autor ou um alvo que não sejam cadeiras desta partida;
 * - um alvo que esteja na equipa do autor - um casco não marca um aliado, e um
 *   documento que o diga descreve uma partida que não aconteceu;
 * - um dano que não seja um decimal.
 *
 * ## O que NÃO é verificado: um prazo
 *
 * Porque não existe. Uma marca não expira, portanto não há campo de tempo
 * restante para validar e não há forma de um documento descrever uma marca
 * "vencida". O que a acaba é uma ordem, e uma ordem não está aqui.
 *
 * ## E o que também NÃO é recusado: quantas são
 *
 * Não há teto. O contrato é explícito - "não existe teto mecânico de stacks" - e
 * um limite aqui seria uma decisão de equilíbrio tomada por um validador.
 */
function decodeBleeding(
  value: unknown,
  index: number,
  teamOf: ReadonlyMap<PvpParticipantId, PvpTeam>,
): PvpBleedingV3 {
  const what = `bleeding[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, BLEEDING_KEYS, what);
  const attacker = raw.attackerParticipantId;
  if (typeof attacker !== 'string' || !teamOf.has(attacker)) {
    refuse(
      'projectile_owner_unknown',
      `${what} was applied by ${String(attacker)}, who is not here`,
    );
  }
  const target = raw.targetParticipantId;
  if (typeof target !== 'string' || !teamOf.has(target)) {
    refuse('projectile_owner_unknown', `${what} sits on ${String(target)}, who is not here`);
  }
  if (teamOf.get(attacker) === teamOf.get(target)) {
    refuse(
      'projectile_owner_team_mismatch',
      `${what} was applied by ${attacker} to ${target}, who is on the same team`,
    );
  }
  return {
    id: bounded(raw.id, `${what}.id`, 0, Number.MAX_SAFE_INTEGER),
    attackerParticipantId: attacker as PvpParticipantId,
    targetParticipantId: target as PvpParticipantId,
    damageMilli: decimal(raw.damageMilli, `${what}.damageMilli`),
    appliedAt: bounded(raw.appliedAt, `${what}.appliedAt`, 0, Number.MAX_SAFE_INTEGER),
    /* A linhagem. Um booleano exato: nada aqui é reparado nem assumido. */
    reflected: boolean(raw.reflected, `${what}.reflected`),
  };
}

function decodeMissile(
  value: unknown,
  index: number,
  teamOf: ReadonlyMap<PvpParticipantId, PvpTeam>,
): PvpMissileV3 {
  const what = `missiles[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, MISSILE_KEYS, what);
  const owner = raw.ownerParticipantId;
  if (typeof owner !== 'string' || !teamOf.has(owner)) {
    refuse('projectile_owner_unknown', `${what} is owned by ${String(owner)}, who is not here`);
  }
  const declared = team(raw.ownerTeam, `${what}.ownerTeam`);
  if (teamOf.get(owner) !== declared) {
    refuse(
      'projectile_owner_team_mismatch',
      `${what} says team ${declared} and ${owner} is on ${String(teamOf.get(owner))}`,
    );
  }
  const target = raw.targetParticipantId;
  if (target !== null) {
    if (typeof target !== 'string' || !teamOf.has(target)) {
      refuse('projectile_owner_unknown', `${what} chases ${String(target)}, who is not here`);
    }
    if (teamOf.get(target) === declared) {
      refuse(
        'projectile_owner_team_mismatch',
        `${what} chases ${target}, who is on its own team ${declared}`,
      );
    }
  }
  const damageMilli = decimal(raw.damageMilli, `${what}.damageMilli`);
  return {
    id: bounded(raw.id, `${what}.id`, 0, Number.MAX_SAFE_INTEGER),
    ownerParticipantId: owner,
    ownerTeam: declared,
    damageMilli,
    entropy: bounded(raw.entropy, `${what}.entropy`, 0, 0xffff_ffff),
    targetParticipantId: target === null ? null : (target as PvpParticipantId),
    x: integer(raw.x, `${what}.x`),
    y: integer(raw.y, `${what}.y`),
    dirX: bounded(raw.dirX, `${what}.dirX`, -VECTOR_SCALE, VECTOR_SCALE),
    dirY: bounded(raw.dirY, `${what}.dirY`, -VECTOR_SCALE, VECTOR_SCALE),
    life: bounded(raw.life, `${what}.life`, 0, PVP_MISSILE_LIFETIME_TICKS),
    /* A linhagem. Um booleano exato: nada aqui é reparado nem assumido. */
    reflected: boolean(raw.reflected, `${what}.reflected`),
  };
}

function decodeRune(value: unknown, index: number): PvpRuneV3 {
  const what = `runes[${index}]`;
  const raw = asObject(value, what);
  requireExactKeys(raw, RUNE_KEYS, what);
  const pad = integer(raw.pad, `${what}.pad`);
  if (pad < 0 || pad >= PVP_RUNE_PADS.length) {
    refuse('unknown_pad', `${what}.pad is ${pad} and the arena has ${PVP_RUNE_PADS.length}`);
  }
  /*
   * **Nenhuma espécie é normalizada. Ou é uma das três, ou é recusada.**
   *
   * Havia aqui uma tolerância para a runa removida, que a lia como pad vazio
   * (gate V28H.H.1). Saiu com a mecânica, e a recusa é agora a resposta certa:
   * um documento que a nomeie descreve uma partida que este jogo não sabe
   * jogar, e carregá-lo em silêncio seria pior do que o recusar.
   */
  const kind = raw.kind;
  if (kind !== null && !(PVP_RUNE_KINDS as readonly string[]).includes(kind as string)) {
    refuse('unknown_rune_kind', `${what}.kind is ${String(kind)}`);
  }
  return {
    pad,
    kind: kind as PvpRuneKind | null,
    readyAt: bounded(raw.readyAt, `${what}.readyAt`, 0, Number.MAX_SAFE_INTEGER),
  };
}

function decodeResult(
  value: unknown,
  participants: readonly DecodedParticipant[],
  ruleset: PvpRuleset,
): PvpResultV3 | null {
  if (value === null) {
    return null;
  }
  const raw = asObject(value, 'result');
  requireExactKeys(raw, RESULT_KEYS, 'result', RESULT_KEYS_ADDED_AFTER_V3);
  const reason = raw.reason;
  if (typeof reason !== 'string' || !(PVP_END_REASONS as readonly string[]).includes(reason)) {
    refuse('unknown_end_reason', `result.reason is ${String(reason)}`);
  }
  const endReason = reason as PvpEndReason;
  const winner = raw.winner === null ? null : team(raw.winner, 'result.winner');
  if (winner !== null && !endReasonMayHaveWinner(endReason)) {
    refuse('result_winner_impossible', `${endReason} may not name a winner`);
  }

  const living = (side: PvpTeam) =>
    participants.some((entry) => entry.parsedTeam === side && BigInt(entry.healthMilli) > 0n);

  if (winner !== null && endReason === 'death') {
    /*
     * A death names the team that still has somebody standing, and only that.
     * Both halves are checked: a "winner" whose ships are all down, and a
     * "loser" with a ship still alive, are the two ways a stored result can
     * describe a match these positions never produced.
     */
    if (!living(winner)) {
      refuse('result_contradicts_health', `${winner} won by death with no ship standing`);
    }
    for (const side of PVP_TEAMS) {
      if (side !== winner && living(side)) {
        refuse('result_contradicts_health', `${side} lost by death with a ship still alive`);
      }
    }
  }
  if (winner === null && endReason === 'mutual_destruction') {
    for (const side of PVP_TEAMS) {
      if (living(side)) {
        refuse('result_contradicts_health', `mutual destruction with ${side} still standing`);
      }
    }
  }
  return {
    winner,
    reason: endReason,
    ticks: bounded(raw.ticks, 'result.ticks', 0, Number.MAX_SAFE_INTEGER),
    abandon: decodeAbandonment(raw.abandon, endReason, ruleset),
  };
}

/**
 * Why an abandonment was one, out of a record that may predate the question.
 *
 * Three answers, and they are not the same answer twice: `undefined` is a record
 * written before this existed, `null` is a record that says this ending was not
 * an abandonment, and an object is the explanation. The first two both decode to
 * `null`, and the loss is real and deliberate - a build that guessed which of
 * the two abandonments an old record described would be putting a sentence on a
 * result screen that nobody ever wrote down.
 */
function decodeAbandonment(
  value: unknown,
  reason: PvpEndReason,
  ruleset: PvpRuleset,
): PvpAbandonmentV3 | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (reason !== 'abandonment') {
    refuse('result_winner_impossible', `${reason} cannot carry an abandonment`);
  }
  const raw = asObject(value, 'result.abandon');
  requireExactKeys(raw, ABANDONMENT_KEYS, 'result.abandon');
  const cause = raw.cause;
  if (typeof cause !== 'string' || !(PVP_ABANDON_CAUSES as readonly string[]).includes(cause)) {
    refuse('unknown_end_reason', `result.abandon.cause is ${String(cause)}`);
  }
  /*
   * The chair is re-parsed against the ruleset rather than trusted as a string.
   * A stored `a4` is not a chair anybody could have sat in, and a result naming
   * one would name nobody on every screen that read it.
   */
  const id = typeof raw.participantId === 'string' ? raw.participantId : '';
  if (pvpParseParticipantId(id, ruleset) === null) {
    refuse('slot_not_in_ruleset', `result.abandon.participantId is not a chair: ${id}`);
  }
  return { cause: cause as PvpAbandonCause, participantId: id };
}

/**
 * Reads a v3 snapshot, or refuses it.
 *
 * Takes `unknown` because that is what comes back out of storage and off a wire:
 * a decoder typed to its own output has already assumed the thing it is meant to
 * be checking.
 *
 * Nothing is repaired. Every refusal below describes a snapshot that would
 * otherwise load into a DIFFERENT match than the one it recorded, which is worse
 * than an error because nobody would see it happen.
 */
export function pvpDecodeSnapshotV3(value: unknown): PvpSnapshotV3 {
  const raw = asObject(value, 'snapshot');
  requireExactKeys(raw, SNAPSHOT_KEYS, 'snapshot');

  if (raw.version !== PVP_SNAPSHOT_V3_VERSION) {
    refuse('unknown_version', `this build reads version ${PVP_SNAPSHOT_V3_VERSION}`);
  }
  /*
   * The document names its own ruleset, and the answer decides how many chairs
   * there are and which ids are chairs at all. A snapshot that named neither -
   * or named something this build does not play - is refused rather than read as
   * the nearest thing.
   */
  if (
    typeof raw.ruleset !== 'string' ||
    !(PVP_SNAPSHOT_V3_RULESETS as readonly string[]).includes(raw.ruleset)
  ) {
    refuse(
      'unsupported_ruleset',
      `version ${PVP_SNAPSHOT_V3_VERSION} describes ${PVP_SNAPSHOT_V3_RULESETS.join(' or ')}, got ${String(raw.ruleset)}`,
    );
  }
  const ruleset = raw.ruleset as PvpRuleset;

  const canonical = pvpCanonicalSeats(ruleset);
  const entries = array(raw.participants, 'participants');
  if (entries.length !== canonical.length) {
    refuse(
      'wrong_participant_count',
      `${ruleset} has ${canonical.length} chairs and the snapshot carries ${entries.length}`,
    );
  }

  const participants = entries.map((entry, index) => decodeParticipant(entry, index, ruleset));

  const seenIds = new Set<string>();
  const seenChairs = new Set<string>();
  for (const participant of participants) {
    if (seenIds.has(participant.participantId)) {
      refuse('duplicate_participant_id', `${participant.participantId} appears twice`);
    }
    seenIds.add(participant.participantId);
    const chair = `${participant.parsedTeam}:${participant.parsedSlot}`;
    if (seenChairs.has(chair)) {
      refuse('duplicate_team_slot', `${chair} was handed out twice`);
    }
    seenChairs.add(chair);
  }

  /*
   * Canonical order is part of the CONTRACT, not a convenience.
   *
   * The encoder always emits team-then-slot, whatever order the roster was built
   * in, so two rooms with identical futures serialise to identical bytes. A
   * decoder that accepted another order - even by sorting it - would make that
   * comparison meaningless, and byte equality is how the continuation test
   * proves a rebuilt room is the same match.
   */
  canonical.forEach((seat, index) => {
    const participant = participants[index];
    if (!participant || participant.participantId !== seat.participantId) {
      refuse(
        'participants_out_of_order',
        `participants[${index}] must be ${seat.participantId}, got ${participant?.participantId ?? 'nothing'}`,
      );
    }
  });

  const teamOf = new Map<PvpParticipantId, PvpTeam>(
    participants.map((participant) => [participant.participantId, participant.parsedTeam]),
  );

  const shots = array(raw.projectiles, 'projectiles');
  if (shots.length > PVP_MAX_PROJECTILES) {
    refuse(
      'too_many_projectiles',
      `a match may hold ${PVP_MAX_PROJECTILES} and the snapshot carries ${shots.length}`,
    );
  }

  /*
   * Os mísseis têm o SEU teto, e a recusa é a mesma disciplina.
   *
   * Não partilham o dos projéteis porque não partilham o limite: um documento
   * com trezentos mísseis e duzentos tiros descreve uma sala legítima no pior
   * caso, e somá-los recusaria uma partida que aconteceu mesmo.
   */
  const missiles = array(raw.missiles, 'missiles');
  if (missiles.length > PVP_MAX_MISSILES) {
    refuse(
      'too_many_projectiles',
      `a match may hold ${PVP_MAX_MISSILES} missiles and the snapshot carries ${missiles.length}`,
    );
  }

  return {
    version: PVP_SNAPSHOT_V3_VERSION,
    ruleset,
    tick: bounded(raw.tick, 'tick', 0, Number.MAX_SAFE_INTEGER),
    participants: participants.map((participant) => plainParticipant(participant)),
    projectiles: shots.map((shot, index) => decodeProjectile(shot, index, teamOf)),
    nextProjectileId: bounded(raw.nextProjectileId, 'nextProjectileId', 0, Number.MAX_SAFE_INTEGER),
    runes: array(raw.runes, 'runes').map((rune, index) => decodeRune(rune, index)),
    rng: rng(raw.rng, 'rng'),
    /*
     * A semente e os fluxos de proc, exigidos pelo formato 7.
     *
     * Um documento anterior não os tem, e é o UPGRADE que lhos dá - nunca este
     * descodificador a inventá-los. Aqui a ausência é uma recusa: um documento
     * carimbado como 7 que não os traga está malformado.
     */
    seed: decimalString(raw.seed, 'seed'),
    procRng: Object.fromEntries(
      Object.entries(asObject(raw.procRng, 'procRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `procRng.${participantId}`),
      ]),
    ),
    /* Os fluxos por salva, exigidos pelo formato 10 e nunca inventados aqui. */
    bombRng: Object.fromEntries(
      Object.entries(asObject(raw.bombRng, 'bombRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `bombRng.${participantId}`),
      ]),
    ),
    /* Os mísseis e o seu contador, exigidos pelo formato 11 (gate V28G.L). */
    missiles: missiles.map((missile, index) => decodeMissile(missile, index, teamOf)),
    /* Os fluxos de alvo, nunca inventados aqui: é o upgrade que lhos dá. */
    missileRng: Object.fromEntries(
      Object.entries(asObject(raw.missileRng, 'missileRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `missileRng.${participantId}`),
      ]),
    ),
    /*
     * Os stacks de veneno e o seu contador, exigidos pelo formato 12 (V28G.M).
     *
     * Sem teto de quantidade, deliberadamente: ver a nota em `decodePoison`.
     */
    poison: array(raw.poison, 'poison').map((stack, index) => decodePoison(stack, index, teamOf)),
    nextPoisonId: bounded(raw.nextPoisonId, 'nextPoisonId', 0, Number.MAX_SAFE_INTEGER),
    /* Os fluxos do veneno, nunca inventados aqui: é o upgrade que lhos dá. */
    poisonRng: Object.fromEntries(
      Object.entries(asObject(raw.poisonRng, 'poisonRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `poisonRng.${participantId}`),
      ]),
    ),
    /*
     * As marcas de sangramento e o seu contador, exigidos pelo formato 13
     * (V28G.N). Sem teto de quantidade: ver a nota em `decodeBleeding`.
     */
    bleeding: array(raw.bleeding, 'bleeding').map((mark, index) =>
      decodeBleeding(mark, index, teamOf),
    ),
    nextBleedingId: bounded(raw.nextBleedingId, 'nextBleedingId', 0, Number.MAX_SAFE_INTEGER),
    /* Os fluxos do sangramento, nunca inventados aqui: é o upgrade que lhos dá. */
    bleedingRng: Object.fromEntries(
      Object.entries(asObject(raw.bleedingRng, 'bleedingRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `bleedingRng.${participantId}`),
      ]),
    ),
    /* E os fluxos DEFENSIVOS, pela mesma disciplina exata (versão 14). */
    reflectRng: Object.fromEntries(
      Object.entries(asObject(raw.reflectRng, 'reflectRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `reflectRng.${participantId}`),
      ]),
    ),
    /* As invocações (versão 21, gate V28H.L). Ausentes = lista vazia. */
    summons: decodeSummons(raw.summons, 'summons'),
    saws: array(raw.saws, 'saws').map((contact, index) => decodeSawContact(contact, index, teamOf)),
    sawRng: Object.fromEntries(
      Object.entries(asObject(raw.sawRng, 'sawRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `sawRng.${participantId}`),
      ]),
    ),
    vitalRng: Object.fromEntries(
      Object.entries(asObject(raw.vitalRng, 'vitalRng')).map(([participantId, state]) => [
        participantId,
        rng(state, `vitalRng.${participantId}`),
      ]),
    ),
    result: decodeResult(raw.result, participants, ruleset),
  };
}

/** Drops the decoder's private fields, so the answer is exactly the format. */
function plainParticipant(participant: DecodedParticipant): PvpParticipantV3 {
  return {
    cards: participant.cards,
    cardCommands: participant.cardCommands,
    cardEffects: participant.cardEffects,
    participantId: participant.participantId,
    team: participant.team,
    slot: participant.slot,
    x: participant.x,
    y: participant.y,
    moveX: participant.moveX,
    moveY: participant.moveY,
    healthMilli: participant.healthMilli,
    aimX: participant.aimX,
    aimY: participant.aimY,
    cooldownMilliTicks: participant.cooldownMilliTicks,
    profile: { ...participant.profile },
    appliedSeq: participant.appliedSeq,
    connected: participant.connected,
    firing: participant.firing,
    effects: { ...participant.effects },
    teleportTo: participant.teleportTo === null ? null : { ...participant.teleportTo },
    detonateBleed: participant.detonateBleed,

    lifestealCarryBps: participant.lifestealCarryBps,

    knockback: participant.knockback === null ? null : { ...participant.knockback },
    sawPhase: participant.sawPhase,
    sawCarryMilli: participant.sawCarryMilli,
  };
}

/** The result a team match ends with, as this format stores it. */
export function pvpTeamResultToV3(result: PvpTeamResult): PvpResultV3 {
  return {
    winner: result.winner,
    reason: result.reason,
    ticks: result.ticks,
    abandon:
      result.abandonment === null
        ? null
        : { cause: result.abandonment.cause, participantId: result.abandonment.participantId },
  };
}

/** And back. The letter is re-branded here and nowhere else. */
export function pvpTeamResultFromV3(result: PvpResultV3, ruleset: PvpRuleset): PvpTeamResult {
  return {
    winner: result.winner === null ? null : team(result.winner, 'result.winner'),
    reason: result.reason,
    ticks: result.ticks,
    /*
     * The chair is re-branded through the parser, exactly as the winning letter
     * is. `decodeAbandonment` already refused anything that is not a chair, so
     * the assertion here is a second reader failing loudly rather than a first
     * reader trusting a string.
     */
    abandonment:
      result.abandon === null
        ? null
        : {
            cause: result.abandon.cause,
            participantId: participantIdFromV3(result.abandon.participantId, ruleset),
          },
  };
}

function participantIdFromV3(value: string, ruleset: PvpRuleset): PvpParticipantId {
  const ref = pvpParseParticipantId(value, ruleset);
  if (ref === null) {
    throw new RangeError(`not a pvp chair: ${value}`);
  }
  return pvpParticipantId(ref.team, ref.slot);
}

/* -------------------------------------------------------------------------- */
/*  The version-4 upgrade                                                     */
/* -------------------------------------------------------------------------- */

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V4_VERSION = 4;

/**
 * **A version-4 document, rewritten as a version-5 one.**
 *
 * ## Why an upgrade rather than a lenient decoder
 *
 * Because a lenient decoder is a decoder that cannot say which format it read.
 * `pvpDecodeSnapshotV3` demands the exact key set and the exact version, and
 * every rule it enforces is written once - so widening it to tolerate two
 * shapes would put "which fields exist" into a branch inside every reader.
 *
 * This is the other answer: a total, explicit, testable rewrite from one
 * version to the next, run BEFORE the decoder and never inside it. A caller
 * that meets a version-4 record upgrades it and then decodes; a caller that
 * meets anything else gets the decoder's own refusal.
 *
 * ## The three fields, and why each default is exact rather than convenient
 *
 * - `profile.primordialHeartCount = '0'`. Version 4 was written before a ship
 *   could carry a heart, so every hull it describes carried none. Zero is not a
 *   fallback here - it is the value those hulls had.
 * - o resto de dano fica AUSENTE. Com zero corações um tiro vale um número
 *   inteiro de pontos, portanto nenhum golpe de uma partida da versão 4 deixou
 *   resto - e desde a versão 16 o formato já não tem onde o guardar, porque a
 *   vida é milli e não há conversão que produza um. Nada está a ser perdoado;
 *   não havia nada para carregar.
 * - `projectile.damageMilli = String(damage * 1000)`. Version 4 stored whole
 *   points, and `x 1000` is the exact conversion into milli. No shot in flight
 *   changes what it will take off.
 *
 * That is what makes this upgrade LOSSLESS in both directions of meaning: the
 * match resumed from the rewritten document is the same match, tick for tick.
 *
 * ## What it does NOT do
 *
 * It does not validate. Every rule - bounds, chairs, owners, canonical order -
 * belongs to the decoder and runs after this, on the rewritten document. This
 * function only reshapes what it can recognise and hands back `null` for
 * anything else, so a corrupt record is refused BY THE DECODER with the decoder's
 * own named code rather than by a rewrite that guessed.
 */
export function pvpUpgradeSnapshotV4(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V4_VERSION) {
    return null;
  }
  if (!Array.isArray(raw.participants) || !Array.isArray(raw.projectiles)) {
    return null;
  }
  const participants = raw.participants.map((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return entry;
    }
    const participant = entry as Record<string, unknown>;
    const profile =
      typeof participant.profile === 'object' && participant.profile !== null
        ? { ...(participant.profile as Record<string, unknown>), primordialHeartCount: '0' }
        : participant.profile;
    return { ...participant, profile };
  });
  const projectiles = raw.projectiles.map((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return entry;
    }
    const { damage, ...rest } = entry as Record<string, unknown>;
    if (typeof damage !== 'number' || !Number.isSafeInteger(damage) || damage < 0) {
      /*
       * Devolvido tal como está, SEM `damageMilli`. O decodificador recusa-o a
       * seguir por lhe faltar uma chave exigida - que é a resposta certa, com o
       * nome certo, em vez de esta função inventar um dano.
       */
      return rest;
    }
    return { ...rest, damageMilli: String(damage * 1000) };
  });
  /*
   * Encadeado, e não reescrito para a versão corrente de uma vez.
   *
   * Este passo sabe o que a versão 4 significava e produz uma versão 5; o passo
   * seguinte sabe o que a versão 5 significava e produz a corrente. Fazer os
   * dois aqui obrigaria esta função a conhecer todos os campos que vierem a
   * seguir, e a cada gate alguém teria de se lembrar de a atualizar - que é
   * exatamente o esquecimento que produz um fallback silencioso.
   */
  return pvpUpgradeSnapshotV5({
    ...raw,
    version: PVP_SNAPSHOT_V5_VERSION,
    participants,
    projectiles,
  });
}

/* -------------------------------------------------------------------------- */
/*  The version-5 upgrade                                                     */
/* -------------------------------------------------------------------------- */

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V5_VERSION = 5;

/**
 * **A version-5 document, rewritten as a version-6 one.**
 *
 * ## O campo, e porque o seu valor por omissão é EXATO
 *
 * `profile.jewels = { entries: [] }`. A versão 5 foi escrita antes de um socket
 * poder conter alguma coisa: nenhuma rota encaixava, nenhum bilhete
 * transportava e nenhuma sala congelava um perfil. Todos os cascos que ela
 * descreve tinham zero joias, por isso o vazio não é um fallback - é o valor
 * que esses cascos tinham.
 *
 * Isto é o que separa uma subida de um `?? EMPTY` espalhado pelos leitores. Um
 * `??` diz "não sei, assume vazio" em toda a parte e para sempre; esta função
 * diz "sei exatamente o que a versão 5 significava" uma vez, num sítio, com uma
 * prova ao lado.
 *
 * ## O que ela NÃO faz
 *
 * Não valida, pela mesma razão que a subida da versão 4 não valida: as regras
 * pertencem ao decodificador e correm a seguir, sobre o documento reescrito.
 * Esta função só reformula o que reconhece e devolve `null` para o resto, para
 * um registo corrompido ser recusado PELO decodificador com o nome dele.
 */
export function pvpUpgradeSnapshotV5(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V5_VERSION) {
    return null;
  }
  if (!Array.isArray(raw.participants)) {
    return null;
  }
  const participants = raw.participants.map((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return entry;
    }
    const participant = entry as Record<string, unknown>;
    const profile =
      typeof participant.profile === 'object' && participant.profile !== null
        ? {
            ...(participant.profile as Record<string, unknown>),
            jewels: { entries: [] },
          }
        : participant.profile;
    return { ...participant, profile };
  });
  /*
   * Devolve um documento da versão 6, e não da 7.
   *
   * Este upgrade acrescenta `jewels` e mais nada. Carimbá-lo com a versão que
   * este build escreve produziria um documento que se diz atual sem ter a
   * semente nem os fluxos de proc - e o descodificador recusá-lo-ia com uma
   * mensagem sobre um campo em falta, escondendo que a cadeia de upgrades é
   * que estava quebrada.
   */
  return pvpUpgradeSnapshotV6({ ...raw, version: PVP_SNAPSHOT_V6_VERSION, participants });
}

/** A versão que precedeu a loteria de proc. Reconhecida, nunca escrita. */
export const PVP_SNAPSHOT_V6_VERSION = 6;

/**
 * **O upgrade 6 → 7: a semente da sala e os fluxos de proc.**
 *
 * ## O que um documento da versão 6 não tem, e porquê
 *
 * A semente. Ela nunca foi precisa antes de existirem fluxos por cadeira: o
 * único gerador era o das runas, e ele já estava semeado quando o documento foi
 * escrito.
 *
 * ## De onde ela sai, e a garantia que isso exige
 *
 * Do **estado persistido do gerador das runas**, que o documento carrega e cuja
 * própria semente é determinística. Duas garantias, e as duas são exigências e
 * não conveniências:
 *
 * 1. o estado é **LIDO, nunca avançado** — nada aqui chama `next` seja o que
 *    for, portanto a sequência futura das runas fica byte a byte igual;
 * 2. o domínio é separado por rótulo versionado e pela cadeira canónica, o que
 *    torna os fluxos de proc independentes entre si e independentes das runas.
 *
 * Isto **não é um gerador partilhado**. Só a semente do upgrade é derivada de
 * estado antigo; a partir daí cada fluxo corre sozinho.
 *
 * ## Idempotência
 *
 * Um v6 atualizado duas vezes dá o mesmo v7: a derivação é uma função pura do
 * documento, e a segunda passagem não reconhece a versão 7 e devolve `null`. E
 * depois do upgrade o escritor nunca volta a escrever v6 - só existe uma versão
 * que este build produz.
 *
 * ## Os projéteis em voo
 *
 * Recebem `jewelProc: null`. Nenhum proc é atribuído retroativamente: um tiro
 * disparado numa partida que não conhecia procs não pode ganhar um agora, e
 * sortear-lhe um seria inventar um efeito que a simulação original nunca teve.
 */
export function pvpUpgradeSnapshotV6(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V6_VERSION) {
    return null;
  }
  const runeRng = raw.rng;
  if (typeof runeRng !== 'object' || runeRng === null) {
    return null;
  }
  const seed = (runeRng as Record<string, unknown>).seed;
  if (typeof seed !== 'string') {
    return null;
  }
  const projectiles = Array.isArray(raw.projectiles)
    ? raw.projectiles.map((entry) =>
        typeof entry === 'object' && entry !== null
          ? { ...(entry as Record<string, unknown>), jewelProc: null }
          : entry,
      )
    : raw.projectiles;
  /* Encadeado, como o 5 → 6 já encadeia: um documento antigo sobe um degrau de
   * cada vez, e nenhum degrau conhece mais do que o seguinte. */
  return pvpUpgradeSnapshotV7({
    ...raw,
    version: PVP_SNAPSHOT_V7_VERSION,
    seed,
    /*
     * VAZIO, e não um fluxo por cadeira já semeado.
     *
     * Um fluxo só nasce quando um casco dispara pela primeira vez, derivado da
     * semente e da sua cadeira canónica. Semeá-los aqui daria o mesmo resultado
     * e obrigaria este upgrade a conhecer a lista de participantes, que é uma
     * dependência que ele não precisa de ter.
     */
    procRng: {},
    projectiles,
  });
}

/** A versão em que um proc tinha id e entropia mas nenhuma magnitude. */
export const PVP_SNAPSHOT_V7_VERSION = 7;

/**
 * 7 → 8: o proc de um projétil passa a carregar a MAGNITUDE congelada.
 *
 * ## Porque os procs em voo caem em vez de serem recalculados
 *
 * Um documento da versão 7 diz que um tiro foi selecionado e não diz quanto
 * valia o corte: a versão 7 só guardava `{ id, entropy }` porque a magnitude era
 * recalculada no impacto. Reconstruí-la aqui exigiria correr a fórmula outra vez
 * - e correr a fórmula é exatamente o que a versão 8 existe para deixar de
 * fazer.
 *
 * É verdade que num DUELO a reconstrução seria exata: `freezeParticipantJewels`
 * recusa depois do tique zero, portanto o perfil no documento é o mesmo que
 * estava lá quando o tiro nasceu. Mas escrever essa fórmula aqui poria de volta,
 * num caminho de migração, a leitura tardia que o gate remove - e um upgrade que
 * conhece a matemática de uma joia é um segundo sítio onde ela vive.
 *
 * Então `null`, e o custo é conhecido e pequeno: os tiros JÁ NO AR no instante
 * exato em que a sala hiberna perdem o seu corte. Não se perde vida já tirada,
 * não se perde um proc já aplicado, e nenhum tiro recebe um corte que não
 * trazia. Uma sala sem projéteis em voo atravessa sem perder nada.
 */
export function pvpUpgradeSnapshotV7(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V7_VERSION) {
    return null;
  }
  const projectiles = Array.isArray(raw.projectiles)
    ? raw.projectiles.map((entry) =>
        typeof entry === 'object' && entry !== null
          ? { ...(entry as Record<string, unknown>), jewelProc: null }
          : entry,
      )
    : raw.projectiles;
  /* Encadeado, como todos os degraus antes dele. */
  return pvpUpgradeSnapshotV8({ ...raw, version: PVP_SNAPSHOT_V8_VERSION, projectiles });
}

/** A versão em que um casco não tinha os restos do Roubo de Vida. */
export const PVP_SNAPSHOT_V8_VERSION = 8;

/**
 * 8 → 9: cada casco ganha os dois restos do Roubo de Vida.
 *
 * ## Zero, e é EXATO em vez de conveniente
 *
 * Uma sala da versão 8 correu num build onde esta joia não fazia nada, portanto
 * nenhum casco dela pode ter ganho uma fração de cura. Zero não é um valor por
 * omissão a preencher um campo: é o que esses cascos realmente tinham.
 *
 * ## O que NÃO é retroativo, e o que passa a valer
 *
 * Nenhuma vida é devolvida. Nenhum golpe já dado é reprocessado. Nenhum casco
 * acorda mais cheio do que adormeceu - os dois campos novos entram a zero e a
 * `health` de cada um é a que o documento trazia.
 *
 * O que muda é daqui para a frente: os golpes SEGUINTES curam. É a mesma coisa
 * que aconteceu no degrau 6 → 7, quando salas antigas passaram a poder tirar
 * procs, e é correto pela mesma razão: o equipamento não mudou - `freezeParticipantJewels`
 * congelou-o no tique zero e ele está no documento - mudou a REGRA que diz o que
 * ele vale, e uma regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV8(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V8_VERSION) {
    return null;
  }
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map((entry) =>
        typeof entry === 'object' && entry !== null
          ? { ...(entry as Record<string, unknown>), lifestealCarryBps: 0, healCarryMilli: 0 }
          : entry,
      )
    : raw.participants;
  /* Encadeado, como todos os degraus antes dele. */
  return pvpUpgradeSnapshotV9({ ...raw, version: PVP_SNAPSHOT_V9_VERSION, participants });
}

/** A versão que precedeu o Núcleo Atômico. Reconhecida, nunca escrita. */
export const PVP_SNAPSHOT_V9_VERSION = 9;

/**
 * **9 → 10: a bomba, o empurrão e o fluxo por salva.**
 *
 * ## Os três valores, e porque nenhum deles é um "valor por omissão"
 *
 * - **`atomicBomb: null` em cada projétil em voo.** Uma sala da versão 9 correu
 *   num build onde esta joia não fazia nada, portanto nenhum tiro dela pode ter
 *   sido uma bomba. Transformar um agora seria trocar-lhe a entidade a meio do
 *   voo: ele mudaria de velocidade, deixaria de bater e passaria a detonar.
 *
 * - **`knockback: null` em cada casco.** Nenhuma explosão houve, portanto
 *   ninguém estava a ser empurrado. Inventar um agora tiraria o controlo a um
 *   piloto por causa de uma bomba que a partida não conheceu.
 *
 * - **`bombRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela
 *   mesma razão que o degrau 6 → 7 deixou `procRng` vazio: um fluxo nasce quando
 *   um casco dispara pela primeira vez, derivado da semente e da sua cadeira
 *   canónica, e semeá-los aqui daria exatamente o mesmo resultado ao preço de
 *   este upgrade ter de conhecer a lista de participantes.
 *
 * ## O que passa a valer daqui para a frente
 *
 * As salvas SEGUINTES podem sortear a bomba. É a mesma fronteira do degrau
 * 8 → 9, e é correta pela mesma razão: o equipamento não mudou - está congelado
 * no documento desde o tique zero - mudou a regra que diz o que ele vale, e uma
 * regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV9(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V9_VERSION) {
    return null;
  }
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map((entry) =>
        typeof entry === 'object' && entry !== null
          ? { ...(entry as Record<string, unknown>), knockback: null }
          : entry,
      )
    : raw.participants;
  const projectiles = Array.isArray(raw.projectiles)
    ? raw.projectiles.map((entry) =>
        typeof entry === 'object' && entry !== null
          ? { ...(entry as Record<string, unknown>), atomicBomb: null }
          : entry,
      )
    : raw.projectiles;
  return pvpUpgradeSnapshotV10({
    ...raw,
    version: PVP_SNAPSHOT_V10_VERSION,
    participants,
    projectiles,
    bombRng: {},
  });
}

/** A versão que precedeu o Arsenal Ígneo. Reconhecida, nunca escrita. */
export const PVP_SNAPSHOT_V10_VERSION = 10;

/**
 * **10 → 11: os mísseis e os fluxos de alvo.**
 *
 * ## Os dois valores, e porque nenhum é um "valor por omissão"
 *
 * - **`missiles: []`.** Uma sala da versão 10 correu num build onde esta joia
 *   não fazia nada, portanto nenhum míssil dela pode ter existido. A lista vazia
 *   é o FACTO, e não uma lacuna: inventar um agora seria pôr no ar uma entidade
 *   autoritativa que a partida nunca lançou, com nove segundos para atingir
 *   alguém.
 *
 * - **`missileRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela
 *   mesma razão que o degrau 9 → 10 deixou `bombRng` vazio: um fluxo nasce
 *   quando um casco precisa dele pela primeira vez, derivado da semente e da sua
 *   cadeira canónica, e semeá-los aqui daria exatamente o mesmo resultado ao
 *   preço de este upgrade ter de conhecer a lista de participantes.
 *
 * ## O que passa a valer daqui para a frente
 *
 * As salvas SEGUINTES podem sortear um míssil. É a mesma fronteira dos degraus
 * anteriores, e é correta pela mesma razão: o equipamento não mudou - está
 * congelado no documento desde o tique zero - mudou a regra que diz o que ele
 * vale, e uma regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV10(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V10_VERSION) {
    return null;
  }
  return pvpUpgradeSnapshotV11({
    ...raw,
    version: PVP_SNAPSHOT_V11_VERSION,
    missiles: [],
    missileRng: {},
  });
}

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V11_VERSION = 11;

/**
 * **11 → 12: os stacks de veneno, o seu contador e os seus fluxos.**
 *
 * ## Os três valores, e porque nenhum é um "valor por omissão"
 *
 * - **`poison: []`.** Uma sala da versão 11 correu num build onde esta joia não
 *   fazia nada, portanto nenhum stack dela pode ter existido. A lista vazia é o
 *   FACTO, e não uma lacuna: inventar um agora seria pôr sobre um casco seis
 *   segundos de dano autoritativo que a partida nunca lhe aplicou.
 *
 * - **`nextPoisonId: 1`.** O primeiro id de uma sala que ainda não aplicou
 *   nenhum. Não é zero por uma razão pequena e concreta: zero é o valor que um
 *   campo em falta produziria num leitor descuidado, e começar em um faz um id
 *   real e uma ausência não se parecerem.
 *
 * - **`poisonRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela
 *   mesma razão que os degraus 9 → 10 e 10 → 11 deixaram os seus vazios: um
 *   fluxo nasce quando um casco precisa dele pela primeira vez, derivado da
 *   semente e da sua cadeira canónica, e semeá-los aqui daria exatamente o mesmo
 *   resultado ao preço de este upgrade ter de conhecer a lista de participantes.
 *
 * ## O que passa a valer daqui para a frente
 *
 * Os acertos SEGUINTES podem envenenar. É a mesma fronteira dos degraus
 * anteriores, e é correta pela mesma razão: o equipamento não mudou - está
 * congelado no documento desde o tique zero - mudou a regra que diz o que ele
 * vale, e uma regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV11(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V11_VERSION) {
    return null;
  }
  return pvpUpgradeSnapshotV12({
    ...raw,
    version: PVP_SNAPSHOT_V12_VERSION,
    poison: [],
    nextPoisonId: 1,
    poisonRng: {},
  });
}

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V12_VERSION = 12;

/**
 * **12 → 13: as marcas de sangramento, o seu contador, os seus fluxos e a ordem
 * por executar.**
 *
 * ## Os quatro valores, e porque nenhum é um "valor por omissão"
 *
 * - **`bleeding: []`.** Uma sala da versão 12 correu num build onde esta joia não
 *   fazia nada, portanto nenhuma marca dela pode ter existido. A lista vazia é o
 *   FACTO, e não uma lacuna: inventar uma agora seria pôr sobre um casco dano
 *   acumulado que a partida nunca lhe aplicou, e dá-lo a um jogador para gastar.
 *
 * - **`nextBleedingId: 1`.** O primeiro id de uma sala que ainda não aplicou
 *   nenhuma. Não é zero pela razão pequena e concreta do degrau anterior: zero é
 *   o valor que um campo em falta produziria num leitor descuidado, e começar em
 *   um faz um id real e uma ausência não se parecerem.
 *
 * - **`bleedingRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela
 *   mesma razão que todos os degraus anteriores deixaram os seus vazios: um
 *   fluxo nasce quando um casco precisa dele pela primeira vez, derivado da
 *   semente e da sua cadeira canónica.
 *
 * - **`detonateBleed: false` em cada casco.** É o único valor que a versão 12
 *   pode ter tido: naquele build não havia botão, não havia campo no comando e
 *   não havia nada para detonar. Escrever `true` daria a um casco uma ordem que
 *   ninguém deu.
 *
 * ## Porque este degrau toca nos PARTICIPANTES, e os outros não tocavam
 *
 * Porque é o primeiro em que o que falta vive num casco e não na sala. Os
 * participantes são reescritos um a um, com o campo acrescentado e mais nada
 * mudado - e um documento cujo `participants` não seja uma lista é devolvido
 * como está, para o descodificador o recusar com o seu próprio erro em vez de
 * este upgrade atirar primeiro.
 *
 * ## O que passa a valer daqui para a frente
 *
 * Os acertos SEGUINTES podem marcar. É a mesma fronteira dos degraus anteriores,
 * e é correta pela mesma razão: o equipamento não mudou - está congelado no
 * documento desde o tique zero - mudou a regra que diz o que ele vale, e uma
 * regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV12(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V12_VERSION) {
    return null;
  }
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map((participant) =>
        typeof participant === 'object' && participant !== null && !Array.isArray(participant)
          ? { ...(participant as Record<string, unknown>), detonateBleed: false }
          : participant,
      )
    : raw.participants;
  /* Encadeado, como todos os degraus antes dele. */
  return pvpUpgradeSnapshotV13({
    ...raw,
    version: PVP_SNAPSHOT_V13_VERSION,
    participants,
    bleeding: [],
    nextBleedingId: 1,
    bleedingRng: {},
  });
}

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V13_VERSION = 13;

/**
 * **13 → 14: os fluxos defensivos, e a linhagem de quatro entidades.**
 *
 * ## Os cinco valores, e porque nenhum é um "valor por omissão"
 *
 * - **`reflectRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela
 *   mesma razão que todos os degraus anteriores deixaram os seus vazios: um
 *   fluxo nasce quando um casco precisa dele pela primeira vez, derivado da
 *   semente e da sua cadeira canónica.
 *
 * - **`reflected: false` em cada projétil, míssil, stack e marca.** É o único
 *   valor que a versão 13 pode ter tido, e é um FACTO e não uma lacuna: naquele
 *   build a Égide Espinhosa não fazia nada, portanto nenhuma entidade dessa sala
 *   pode ter nascido de uma reflexão.
 *
 *   Escrever `true` seria pior do que escrever um número errado: daria a uma
 *   bomba em voo uma imunidade que ninguém lhe ganhou, e tirava ao adversário
 *   uma interceção a que ele tinha direito.
 *
 * ## Porque este degrau toca em QUATRO listas, e os outros tocavam em uma
 *
 * Porque a linhagem não é um estado da sala - é uma propriedade de cada payload
 * que pode ter nascido virado, e há quatro tipos de payload que persistem. As
 * quatro listas são reescritas item a item, com o campo acrescentado e mais nada
 * mudado, e uma lista que não seja uma lista é devolvida como está para o
 * descodificador a recusar com o seu próprio erro em vez de este upgrade atirar
 * primeiro.
 *
 * ## O que passa a valer daqui para a frente
 *
 * As instâncias hostis SEGUINTES podem ser intercetadas. É a mesma fronteira dos
 * degraus anteriores, e é correta pela mesma razão: o equipamento não mudou -
 * está congelado no documento desde o tique zero - mudou a regra que diz o que
 * ele vale, e uma regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV13(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V13_VERSION) {
    return null;
  }
  const original = (list: unknown): unknown =>
    Array.isArray(list)
      ? list.map((entry) =>
          typeof entry === 'object' && entry !== null && !Array.isArray(entry)
            ? { ...(entry as Record<string, unknown>), reflected: false }
            : entry,
        )
      : list;
  return pvpUpgradeSnapshotV14({
    ...raw,
    version: PVP_SNAPSHOT_V14_VERSION,
    projectiles: original(raw.projectiles),
    missiles: original(raw.missiles),
    poison: original(raw.poison),
    bleeding: original(raw.bleeding),
    reflectRng: {},
  });
}

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V14_VERSION = 14;

/**
 * **14 → 15: as órbitas, os restos, os relógios de contacto e o fluxo.**
 *
 * ## Os cinco valores, e porque nenhum é um "valor por omissão"
 *
 * - **`saws: []`.** Uma sala da versão 14 correu num build onde o Diamante
 *   Serrilhado não fazia nada, portanto nenhuma lâmina dela pode ter tocado
 *   ninguém. A lista vazia é o FACTO. Inventar um relógio agora seria dar a uma
 *   serra um intervalo já gasto - ou, pior, dar-lhe um contacto imediato que a
 *   partida nunca lhe deu.
 *
 * - **`sawRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela mesma
 *   razão que todos os degraus anteriores deixaram os seus vazios: um fluxo
 *   nasce quando um casco precisa dele pela primeira vez, derivado da semente e
 *   da sua cadeira canónica.
 *
 * - **`sawPhase: 0` em cada casco.** É a fase inicial de TODA a gente, e não um
 *   valor de recurso: o contrato manda que a órbita comece em zero para todos,
 *   determinística e independente da ordem do roster. Um documento da versão 14
 *   descreve uma sala cujas serras nunca giraram, e zero é exatamente onde elas
 *   estariam.
 *
 * - **`sawCarryMilli: '0'` em cada casco.** Nenhum contacto houve, portanto
 *   nenhum resto ficou por pagar. String decimal, como o formato o guarda.
 *
 * ## O que passa a valer daqui para a frente
 *
 * As lâminas SEGUINTES cortam. É a mesma fronteira dos degraus anteriores, e é
 * correta pela mesma razão: o equipamento não mudou - está congelado no
 * documento desde o tique zero - mudou a regra que diz o que ele vale, e uma
 * regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV14(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V14_VERSION) {
    return null;
  }
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map((participant) =>
        typeof participant === 'object' && participant !== null && !Array.isArray(participant)
          ? {
              ...(participant as Record<string, unknown>),
              sawPhase: SAW_INITIAL_PHASE,
              sawCarryMilli: '0',
            }
          : participant,
      )
    : raw.participants;
  return pvpUpgradeSnapshotV15({
    ...raw,
    version: PVP_SNAPSHOT_V15_VERSION,
    participants,
    saws: [],
    sawRng: {},
  });
}

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V15_VERSION = 15;

/**
 * **15 → 16: a vida passa a MILLI, e o documento deixa de a truncar.**
 *
 * ## Porque o formato tinha de mudar
 *
 * Porque ele guardava pontos inteiros, e nenhuma vida legal é um ponto inteiro.
 * A auditoria do gate V28G.S mediu-o no catálogo: o `flatMaxHealth` de um
 * componente é sorteado por MILLI sobre `[2 000, 12 000]` e o
 * `maxHealthPercent` por BPS sobre `[150, 900]`. Um casco de 102,999 era
 * escrito como 102, e reabria a partida mais fraco do que a jogou.
 *
 * ## A travessia é uma multiplicação EXATA por mil
 *
 * E não um palpite. Um documento da versão 15 nasceu num build onde a vida era
 * inteira por construção, portanto 102 significa exatamente 102 000 milli - não
 * "algures entre 102 000 e 102 999". Nenhuma fração se perdeu, porque nenhuma
 * podia existir.
 *
 * Isso vale para as duas grandezas: o teto do perfil e a vida corrente de cada
 * casco. As duas são multiplicadas pela mesma constante, e por isso a razão
 * entre elas - que é o que a barra desenha - fica exatamente a mesma.
 *
 * ## Os dois restos que saem
 *
 * `damageCarryMilli` e `healCarryMilli` existiam para uma coisa só: segurar a
 * fração perdida ao converter milli-dano e milli-cura numa barra INTEIRA. Sem
 * essa conversão não há fração a segurar, e mantê-los seria persistir dois
 * campos que nunca mais seriam gastos.
 *
 * Descartá-los não perde nada de uma partida em curso: o que eles guardavam era
 * menos de um ponto de vida, e a vida que o documento traz já é o resultado de
 * todos os golpes que foram aplicados. `lifestealCarryBps` FICA - ele guarda
 * uma fração de um MILÉSIMO, que continua a não caber na vida.
 */
export function pvpUpgradeSnapshotV15(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V15_VERSION) {
    return null;
  }
  const participants = Array.isArray(raw.participants)
    ? raw.participants.map((participant) => {
        if (typeof participant !== 'object' || participant === null || Array.isArray(participant)) {
          return participant;
        }
        const entry = { ...(participant as Record<string, unknown>) };
        const profile = entry.profile;
        if (typeof profile === 'object' && profile !== null && !Array.isArray(profile)) {
          const hull = { ...(profile as Record<string, unknown>) };
          /*
           * Só o que EXISTE é convertido.
           *
           * Uma bancada constrói um documento antigo carimbando uma versão
           * baixa num documento novo, e nesse caso o campo já é milli. Escrever
           * `undefined` por cima dele seria o degrau a apagar a vida que ele
           * viera guardar.
           */
          if (hull.maxHealth !== undefined) {
            hull.maxHealthMilli = milliOf(hull.maxHealth);
            delete hull.maxHealth;
          }
          entry.profile = hull;
        }
        if (entry.health !== undefined) {
          entry.healthMilli = milliOf(entry.health);
          delete entry.health;
        }
        delete entry.damageCarryMilli;
        delete entry.healCarryMilli;
        return entry;
      })
    : raw.participants;
  return pvpUpgradeSnapshotV16({ ...raw, version: PVP_SNAPSHOT_V16_VERSION, participants });
}

/** The version this upgrade reads. Nothing else is accepted. */
export const PVP_SNAPSHOT_V16_VERSION = 16;

/**
 * **16 → 17: o fluxo do Coração Vital.**
 *
 * ## Um valor, e ele não é um "valor por omissão"
 *
 * **`vitalRng: {}`.** Vazio, e não um fluxo por cadeira já semeado — pela mesma
 * razão que todos os degraus anteriores deixaram os seus vazios: um fluxo nasce
 * quando um casco precisa dele pela primeira vez, derivado da semente da sala e
 * da sua cadeira canónica. Um mapa pré-preenchido aqui seria este degrau a
 * decidir o estado de um gerador que a partida ainda não usou.
 *
 * ## Porque não entra mais nada
 *
 * Porque a cura não guarda estado nenhum. Não há resto - a magnitude divide uma
 * vez e no fim, portanto ela nasce em milli inteiro - não há pedido pendente,
 * não há alvo escolhido à espera e não há relógio. A vida que a cura pôs já está
 * em `healthMilli`, que o documento sempre carregou.
 *
 * ## O que passa a valer daqui para a frente
 *
 * As salvas SEGUINTES podem curar. É a mesma fronteira dos degraus anteriores, e
 * é correta pela mesma razão: o equipamento não mudou - está congelado no
 * documento desde o tique zero - mudou a regra que diz o que ele vale, e uma
 * regra nova aplica-se a partir da fronteira em que entra.
 */
export function pvpUpgradeSnapshotV16(value: unknown): unknown | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;
  if (raw.version !== PVP_SNAPSHOT_V16_VERSION) {
    return null;
  }
  return { ...raw, version: PVP_SNAPSHOT_V3_VERSION, vitalRng: {} };
}

/**
 * Pontos inteiros vezes mil, como uma string decimal.
 *
 * Um valor que não é um inteiro seguro atravessa INTACTO, de propósito: o
 * decodificador é quem recusa um documento malformado, e um degrau de upgrade
 * que inventasse um número esconderia a recusa em vez de a deixar acontecer.
 */
function milliOf(value: unknown): unknown {
  return typeof value === 'number' && Number.isSafeInteger(value)
    ? (BigInt(value) * 1000n).toString(10)
    : value;
}
